import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { IconArrowLeft, IconPlayerPlay, IconPlayerPause, IconX, IconLock, IconMap, IconVideo, IconDiamond, IconDownload, IconUsers, IconRuler2, IconClock, IconFlag, IconMountain, IconActivity, IconTrophy, IconCalendar, IconRoute, IconCamera, IconRun, IconChevronRight, IconSettings, IconTrash, IconLoader2 } from '@tabler/icons-react';
import { doc, getDoc, collection, onSnapshot, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, list, getDownloadURL, uploadBytes } from 'firebase/storage';
import { db, storage, functions } from '../firebase';
import { useAdminView } from '../contexts/AdminViewContext';
import ActivityMap from '../components/ActivityMap';
import AutoAppendChallengePopup from '../components/AutoAppendChallengePopup';
import RouteOverviewMinimap from '../components/RouteOverviewMinimap';
import MobileRouteGlobeButton from '../components/MobileRouteGlobeButton';
import FinisherMedalModal from '../components/FinisherMedalModal';
import { useChallengeTheme } from '../hooks/useChallengeTheme';
import { getFinisherMedalUrl } from '../data/challengeCatalog';
import stravaConnectButton from '../assets/strava/btn_strava_connect_with_orange.svg';
import JapaneseLogo from '../assets/logos/Japanese_Logo.png';
import { usePostHog } from 'posthog-js/react';
import html2canvas from 'html2canvas';
import {
  getChallengeTotalDistanceMeters,
  isChallengeParticipantComplete,
} from '../utils/challengeProgress';
import 'mapbox-gl/dist/mapbox-gl.css';
import './GpxMapPage.css';

// Mapbox access token
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA';
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

// Default route colors for multi-segment activity highlighting. The actual
// values used at runtime come from the per-challenge theme via routeColorsRef
// (set inside the component), falling back to these when no challenge theme
// is active (legacy maps, unknown configId).
const ROUTE_COLOR_CURRENT = '#901D13';   // Dark vermillion — current activity
const ROUTE_COLOR_OTHER = '#C4534A';     // Lighter vermillion — other activities

// Tile zoom range for custom tiles (with fade zones)
const CUSTOM_TILE_MIN_ZOOM = 12.5;
const CUSTOM_TILE_MAX_ZOOM = 13.5;
const CUSTOM_TILE_FADE_IN_START = 12.0;   // Start fading in
const CUSTOM_TILE_FADE_OUT_END = 14.0;    // Finish fading out
const CUSTOM_TILE_ZOOM = 13;

// Default camera settings
const DEFAULT_CAMERA_ALTITUDE = 800;
const DEFAULT_CAMERA_DISTANCE = 1600;

// 3D Terrain style
const TERRAIN_3D_STYLE = 'mapbox://styles/samuelcollin3333/cml90z507006x01slfbll7s29';

// Scrub sensitivity: meters moved along route per pixel of drag
const DRAG_METERS_PER_PIXEL = 50;

// Bearing smoothing: number of historical bearings to average
const BEARING_HISTORY_SIZE = 8;

/**
 * Calculate a position offset from a point in a given direction
 * @param {[number, number]} position - [lng, lat]
 * @param {number} bearing - Direction in degrees (0 = north, 90 = east)
 * @param {number} distance - Distance in meters
 * @returns {[number, number]} - New [lng, lat] position
 */
const offsetPosition = (position, bearing, distance) => {
  const R = 6371000; // Earth's radius in meters
  const bearingRad = (bearing * Math.PI) / 180;
  const lat1 = (position[1] * Math.PI) / 180;
  const lng1 = (position[0] * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distance / R) +
    Math.cos(lat1) * Math.sin(distance / R) * Math.cos(bearingRad)
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearingRad) * Math.sin(distance / R) * Math.cos(lat1),
    Math.cos(distance / R) - Math.sin(lat1) * Math.sin(lat2)
  );

  return [(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
};

/**
 * Calculate bearing (heading) between two points in degrees
 */
const calculateBearing = (lon1, lat1, lon2, lat2) => {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  let bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360;
};

/**
 * Calculate cumulative distances for each point in meters
 */
const calculateCumulativeDistances = (coordinates) => {
  const distances = [0];
  let totalDistance = 0;

  for (let i = 1; i < coordinates.length; i++) {
    const [lon1, lat1] = coordinates[i - 1];
    const [lon2, lat2] = coordinates[i];

    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    totalDistance += R * c;
    distances.push(totalDistance);
  }

  return distances;
};

/**
 * Get position at a specific distance along the route
 */
const getPositionAtDistance = (coordinates, cumulativeDistances, distance) => {
  const totalDistance = cumulativeDistances[cumulativeDistances.length - 1];
  const clampedDistance = Math.max(0, Math.min(distance, totalDistance));

  // Binary search for the segment
  let low = 0;
  let high = cumulativeDistances.length - 1;
  while (low < high - 1) {
    const mid = Math.floor((low + high) / 2);
    if (cumulativeDistances[mid] <= clampedDistance) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const segmentStart = cumulativeDistances[low];
  const segmentEnd = cumulativeDistances[high];
  const segmentLength = segmentEnd - segmentStart;

  if (segmentLength === 0) {
    return coordinates[low];
  }

  const t = (clampedDistance - segmentStart) / segmentLength;
  const [lng1, lat1] = coordinates[low];
  const [lng2, lat2] = coordinates[high];

  return [
    lng1 + t * (lng2 - lng1),
    lat1 + t * (lat2 - lat1)
  ];
};

/**
 * Get bearing at a specific distance along the route
 */
const getBearingAtDistance = (coordinates, cumulativeDistances, distance) => {
  const totalDistance = cumulativeDistances[cumulativeDistances.length - 1];
  const clampedDistance = Math.max(0, Math.min(distance, totalDistance));

  // Find segment
  let segmentIndex = 0;
  for (let i = 1; i < cumulativeDistances.length; i++) {
    if (cumulativeDistances[i] >= clampedDistance) {
      segmentIndex = i - 1;
      break;
    }
    segmentIndex = i - 1;
  }

  const nextIndex = Math.min(segmentIndex + 1, coordinates.length - 1);
  const [lng1, lat1] = coordinates[segmentIndex];
  const [lng2, lat2] = coordinates[nextIndex];

  return calculateBearing(lng1, lat1, lng2, lat2);
};

const escapeHtml = (unsafe) => {
  return String(unsafe)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

const normalizeHexColor = (value, fallback = '#6b6258') => {
  const input = String(value || '').trim();
  if (!input) return fallback;

  const shortMatch = input.match(/^#?([0-9a-fA-F]{3})$/);
  if (shortMatch) {
    const [r, g, b] = shortMatch[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }

  const longMatch = input.match(/^#?([0-9a-fA-F]{6})$/);
  if (longMatch) {
    return `#${longMatch[1].toLowerCase()}`;
  }

  return fallback;
};

const sanitizeRouteMapStyleChanges = (changes) => {
  if (!Array.isArray(changes)) return [];

  return changes
    .map((change) => ({
      distanceMeters: Math.max(0, Number(change?.distanceMeters) || 0),
      terrainExaggeration: Math.max(0, Number(change?.terrainExaggeration) || 0),
      placeLabelColor: normalizeHexColor(change?.placeLabelColor)
    }))
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
};

const PLACE_LABEL_INCLUDE_RE = /(place|settlement|country|state|locality|neighbou?r|village|town|city|hamlet|natural|water|marine|poi|airport|label)/i;
const PLACE_LABEL_EXCLUDE_RE = /(road|street|motorway|shield|path|rail|ferry|transit|aerialway|runway|building|landuse|contour)/i;

const isPlaceLabelLayer = (layer) => {
  if (!layer || layer.type !== 'symbol') return false;
  const textField = layer.layout && layer.layout['text-field'];
  if (!textField) return false;
  const id = String(layer.id || '');
  const sourceLayer = String(layer['source-layer'] || '');
  const probe = `${id} ${sourceLayer}`;
  return PLACE_LABEL_INCLUDE_RE.test(probe) && !PLACE_LABEL_EXCLUDE_RE.test(probe);
};

// Fallback act used if the catalog has no entry for the current challenge
// (e.g. legacy / unknown configIds). Single section, untitled.
const DEFAULT_ACTS = [
  { name: null, style: 'Mythic', startCp: 0, endCp: 999 },
];

// Build the act/style list for a challenge. Japan keeps its hand-curated
// 4-act narrative (from catalog.acts). Other challenges get a 2-act split
// at `switchCheckpointIndex` using styles[0] and styles[1], no titles.
const buildActsForChallenge = (catalogEntry, checkpointCount) => {
  if (catalogEntry?.acts && catalogEntry.acts.length > 0) {
    return catalogEntry.acts;
  }
  if (!catalogEntry || !checkpointCount) return DEFAULT_ACTS;
  const lastIdx = Math.max(0, checkpointCount - 1);
  const switchIdx = Number.isInteger(catalogEntry.switchCheckpointIndex)
    ? Math.min(Math.max(catalogEntry.switchCheckpointIndex, 1), lastIdx)
    : null;
  const [styleA, styleB] = catalogEntry.styles || [];
  if (switchIdx == null || !styleB) {
    return [{ name: null, style: styleA || 'Mythic', startCp: 0, endCp: lastIdx }];
  }
  return [
    { name: null, style: styleA, startCp: 0, endCp: switchIdx - 1 },
    { name: null, style: styleB, startCp: switchIdx, endCp: lastIdx },
  ];
};

/**
 * Find the distance along the route where a checkpoint is located.
 * Finds the closest route point to the checkpoint's lon/lat.
 */
const findCheckpointDistanceOnRoute = (coordinates, cumulativeDistances, checkpoint) => {
  if (!checkpoint || typeof checkpoint.lon !== 'number' || typeof checkpoint.lat !== 'number') {
    return null;
  }
  let bestIdx = 0;
  let bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < coordinates.length; i++) {
    const [lng, lat] = coordinates[i];
    const R = 6371000;
    const dLat = (checkpoint.lat - lat) * Math.PI / 180;
    const dLon = (checkpoint.lon - lng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat * Math.PI / 180) * Math.cos(checkpoint.lat * Math.PI / 180) *
      Math.sin(dLon / 2) ** 2;
    const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return cumulativeDistances[bestIdx];
};

/**
 * Get the act covering a checkpoint index, given the challenge's act list.
 */
const getActForCheckpointIndex = (acts, cpIndex) => {
  const list = acts && acts.length ? acts : DEFAULT_ACTS;
  return list.find(a => cpIndex >= a.startCp && cpIndex <= a.endCp) || list[list.length - 1];
};

const getStyleForDistance = (acts, distanceMeters, checkpoints, checkpointDistances) => {
  const list = acts && acts.length ? acts : DEFAULT_ACTS;
  let lastPassedIdx = -1;
  for (let i = 0; i < checkpointDistances.length; i++) {
    if (distanceMeters >= checkpointDistances[i]) {
      lastPassedIdx = i;
    }
  }
  if (lastPassedIdx < 0) return list[0].style;
  return getActForCheckpointIndex(list, lastPassedIdx).style;
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

// Timeline checkpoint card (right column — image-centric, like map popup)
const TimelineCheckpointCard = ({ event, onSelect }) => {
  const { checkpoint: cp, globalIdx, isUnlocked, distance, athletesReached } = event;
  return (
    <div
      className={`tl-cp-card ${isUnlocked ? 'unlocked' : 'locked'}`}
      onClick={() => {
        if (isUnlocked) {
          onSelect({ ...cp, globalIdx, cpDistance: distance });
        }
      }}
    >
      <div className="tl-cp-image">
        {isUnlocked ? (
          cp.imageUrl ? (
            <img src={cp.imageUrl} alt={cp.name || `Checkpoint ${globalIdx + 1}`} loading="lazy" />
          ) : (
            <div className="tl-cp-placeholder">{globalIdx + 1}</div>
          )
        ) : (
          <div className="tl-cp-fog">{globalIdx + 1}</div>
        )}
      </div>
      <div className="tl-cp-body">
        <div className="tl-cp-title">{isUnlocked ? (cp.name || `Checkpoint ${globalIdx + 1}`) : `Checkpoint ${globalIdx + 1}`}</div>
        <div className="tl-cp-meta">
          Checkpoint {globalIdx + 1} &middot; {(distance / 1000).toFixed(1)} km
          {athletesReached > 0 && (
            <span className="tl-cp-athletes"><IconUsers size={11} /> {athletesReached}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Timeline activity card (left column — image-centric with Strava/generated map)
const TimelineActivityCard = ({ event, onCreateMap, onOpenGeneratedMap, onFlyover }) => {
  const {
    enrichedActivity,
    isLoaded,
    generatedMapUrl,
    generatedMapRequestId,
    distanceMeters,
    activityDate,
  } = event;

  if (!isLoaded && !generatedMapUrl && !generatedMapRequestId) {
    return (
      <div className="tl-act-card tl-act-skeleton">
        <div className="tl-act-image-skeleton" />
        <div className="tl-act-body">
          <div className="tl-act-skeleton-line" style={{ width: '70%' }} />
          <div className="tl-act-skeleton-line" style={{ width: '45%' }} />
        </div>
      </div>
    );
  }

  const activity = enrichedActivity;
  const distanceValue = isLoaded ? activity?.distance : distanceMeters;
  const movingTimeValue = isLoaded ? activity?.moving_time : null;
  const dateValue = isLoaded ? activity?.start_date : activityDate;
  const parsedDate = dateValue?.toDate ? dateValue.toDate() : (dateValue ? new Date(dateValue) : null);
  const handleFlyoverClick = () => {
    if (typeof onFlyover === 'function') {
      onFlyover(event);
    }
  };

  return (
    <div
      className="tl-act-card"
      onClick={handleFlyoverClick}
      style={typeof onFlyover === 'function' ? { cursor: 'pointer' } : undefined}
    >
      <div
        className="tl-act-image"
      >
        {generatedMapUrl ? (
          <img src={generatedMapUrl} alt="Generated map" loading="lazy" />
        ) : activity?.map?.summary_polyline ? (
          <ActivityMap activity={activity} width={220} height={160} />
        ) : (
          <div className="tl-act-image-skeleton" />
        )}
        {typeof onFlyover === 'function' && (
          <div className="tl-act-play-overlay">
            <IconPlayerPlay size={28} />
          </div>
        )}
      </div>
      <div className="tl-act-body">
        <div className="tl-act-stats">
          {(distanceValue / 1000).toFixed(1)} km
          {movingTimeValue && (
            <> &middot; {formatDuration(movingTimeValue)}</>
          )}
        </div>
        <div className="tl-act-date">
          {parsedDate ? parsedDate.toLocaleDateString() : ''}
        </div>
      </div>
      {generatedMapRequestId ? (
        <button
          className="tl-story-create-btn"
          onClick={(e) => {
            e.stopPropagation();
            onOpenGeneratedMap(generatedMapRequestId);
          }}
        >
          <IconMap size={13} /> View Map
        </button>
      ) : (
        <button
          className="tl-story-create-btn"
          onClick={(e) => {
            e.stopPropagation();
            onCreateMap(activity);
          }}
        >
          <IconMap size={13} /> Create Map
        </button>
      )}
    </div>
  );
};

// Center column story block — narrative text for each event
const TimelineStoryBlock = ({ event, onCreateMap, userProgress }) => {
  if (event.type === 'progress') {
    return (
      <div className="tl-story-block tl-story-progress">
        <div className="tl-story-text">
          You are here &mdash; <strong>{(userProgress / 1000).toFixed(1)} km</strong>
        </div>
      </div>
    );
  }

  if (event.type === 'activity') {
    const { enrichedActivity, isLoaded, distanceMeters } = event;
    if (!isLoaded) {
      return (
        <div className="tl-story-block tl-story-activity">
          <div className="tl-story-skeleton-line" />
        </div>
      );
    }
    const activity = enrichedActivity;
    return (
      <div className="tl-story-block tl-story-activity">
        <div className="tl-story-text">
          You moved <strong>{(activity.distance / 1000).toFixed(1)} km</strong> during <strong>&ldquo;{activity.name}&rdquo;</strong>
        </div>
      </div>
    );
  }

  if (event.type === 'checkpoint') {
    const { checkpoint: cp, globalIdx, isUnlocked, distance } = event;
    if (isUnlocked) {
      return (
        <div className="tl-story-block tl-story-checkpoint">
          <div className="tl-story-text">
            You&rsquo;ve reached <strong>{cp.name || `Checkpoint ${globalIdx + 1}`}</strong>
          </div>
          {cp.desc && (
            <div className="tl-story-excerpt">
              &ldquo;{cp.desc.length > 120 ? cp.desc.slice(0, 120) + '...' : cp.desc}&rdquo;
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="tl-story-block tl-story-checkpoint tl-story-locked">
        <div className="tl-story-text">
          <strong>Checkpoint {globalIdx + 1}</strong> &mdash; {(distance / 1000).toFixed(1)} km
        </div>
      </div>
    );
  }

  return null;
};

const makePossessive = (name) => {
  if (!name) return '';
  return name.endsWith('s') ? `${name}'` : `${name}'s`;
};

const GpxMapPage = ({ user, embedConfigId, viewerOnly = false, autoplay = false, projectedDistanceMeters }) => {
  // When embedded (e.g. the /challenge "Aha" hero), the configId arrives as a
  // prop and the page renders in a chrome-less viewer mode. On the real
  // /challenge/:configId route these props are all undefined/false, so behavior
  // is unchanged.
  const params = useParams();
  const configId = embedConfigId || params.configId;
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAdminView();
  const posthog = usePostHog();
  const { theme: challengeTheme, surfaces: challengeSurfaces, switchCheckpointIndex, cssVars: themeCssVars, entry: challengeCatalogEntry } = useChallengeTheme(configId);
  // Theme-driven route colours, read by updateRouteGradient (which has empty
  // deps for performance). Updated whenever the theme changes.
  const routeColorsRef = useRef({ current: ROUTE_COLOR_CURRENT, other: ROUTE_COLOR_OTHER });
  useEffect(() => {
    routeColorsRef.current = {
      current: challengeTheme.primaryDark || ROUTE_COLOR_CURRENT,
      other: challengeTheme.primary || ROUTE_COLOR_OTHER,
    };
  }, [challengeTheme]);
  const challengePageLoadedRef = useRef(false);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const puckMarkerRef = useRef(null);
  const checkpointMarkersRef = useRef([]);
  const checkpointDistancesRef = useRef([]);
  const openCheckpointPopupsRef = useRef(new Set());
  const activityMarkersRef = useRef([]);
  const activityDistanceRangesRef = useRef([]);
  const openActivityPopupsRef = useRef(new Set());
  const currentActivityIdxRef = useRef(-1);
  const animationRef = useRef(null);
  const flyoverAnimationRef = useRef(null);
  const flyoverRunIdRef = useRef(0);
  const currentBearingRef = useRef(0);

  // Cumulative distances ref for efficient position lookup
  const cumulativeDistancesRef = useRef(null);

  // Bearing smoothing refs
  const bearingHistoryRef = useRef([]);

  // Scrub/drag refs
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartDistanceRef = useRef(0);
  const currentDistanceRef = useRef(0); // Tracks currentDistance to avoid stale closures

  // Camera settings refs (for real-time updates without stale closures)
  const cameraAltitudeRef = useRef(DEFAULT_CAMERA_ALTITUDE);
  const cameraDistanceRef = useRef(DEFAULT_CAMERA_DISTANCE);

  const [config, setConfig] = useState(null);
  // Acts are derived from the catalog + config: Japan uses its 4-act narrative,
  // other challenges get a 2-act split at the switch checkpoint with style
  // labels only (no act titles). Falls back to a single default act when the
  // config isn't loaded yet.
  const challengeActs = useMemo(
    () => buildActsForChallenge(challengeCatalogEntry, config?.route?.checkpoints?.length),
    [challengeCatalogEntry, config?.route?.checkpoints?.length]
  );
  // Short display name without a leading "The " — used in copy like
  // "Samuel's Nakasendo Travelog" / "You conquered the Continental Divide".
  const challengeShortName = useMemo(() => {
    const name = challengeCatalogEntry?.title || config?.name || 'Mythic';
    return name.replace(/^The\s+/i, '');
  }, [challengeCatalogEntry, config?.name]);
  // The end-of-route checkpoint name ("Kyoto" for Nakasendo, "Grand Lake"
  // for the Continental Divide, etc.). Falls back gracefully while the
  // config is loading.
  const finalCheckpointName = useMemo(() => {
    const checkpoints = config?.route?.checkpoints;
    if (!Array.isArray(checkpoints) || !checkpoints.length) return 'the finish';
    const end = checkpoints.find(cp => cp?.isEnd);
    return end?.name || checkpoints[checkpoints.length - 1]?.name || 'the finish';
  }, [config?.route?.checkpoints]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Lazy tile loading refs
  const tilesConfigRef = useRef(null);

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [currentDistance, setCurrentDistance] = useState(0);
  const [isScrubMode, setIsScrubMode] = useState(false);

  // Challenge state
  const [hasJoined, setHasJoined] = useState(false);
  const [participants, setParticipants] = useState([]);
  // Surfaces join failures on the onboarding profile step, which is where the
  // join now happens.
  const [joinError, setJoinError] = useState('');
  // The user's active challenge anywhere in the app (null if none). Used to
  // gate the join button and restart action — one challenge at a time.
  const [activeChallengeInfo, setActiveChallengeInfo] = useState(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [restartError, setRestartError] = useState(null);

  // Onboarding modal state (0=closed, 1=strava, 2=profile+join, 3=activity)
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [profileName, setProfileName] = useState('');
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [importingActivity, setImportingActivity] = useState(false);
  const [latestActivity, setLatestActivity] = useState(null);
  const [loadingLatestActivity, setLoadingLatestActivity] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const participantMarkersRef = useRef({});
  const [showCheckpoints, setShowCheckpoints] = useState(false);
  const [showStatsBar, setShowStatsBar] = useState(false);
  const [tilesIndexVersion, setTilesIndexVersion] = useState(0);
  const [tileLoadingMode, setTileLoadingMode] = useState(() => (
    // Embedded viewer uses dynamic tiles: prefetch serves transparent
    // placeholders while its index builds and re-adds the layer on each bump,
    // which flickers. Dynamic lets Mapbox fetch the public z13 tiles directly.
    viewerOnly ? 'dynamic' : (localStorage.getItem('gpxMap_tileLoadingMode') || 'prefetch')
  ));
  const [tileProgress, setTileProgress] = useState({ loaded: 0, total: 0 });

  // Checkpoints & My Journey state
  const [participantData, setParticipantData] = useState(null);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [isDownloadingCheckpoint, setIsDownloadingCheckpoint] = useState(false);
  const [showMedalModal, setShowMedalModal] = useState(false);
  const [journeyActivities, setJourneyActivities] = useState([]);
  const [activityGenerationMap, setActivityGenerationMap] = useState({});
  const [isLoadingJourney, setIsLoadingJourney] = useState(false);
  const [journeyLoaded, setJourneyLoaded] = useState(false);
  const [rewardedCheckpoints, setRewardedCheckpoints] = useState([]);
  const journeySectionRef = useRef(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiFiredRef = useRef(false);
  const [showKyotoConfetti, setShowKyotoConfetti] = useState(false);
  const kyotoConfettiFiredRef = useRef(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [deletingActivityId, setDeletingActivityId] = useState(null);
  const [showAutoAppendPopup, setShowAutoAppendPopup] = useState(false);
  const [showFinishersModal, setShowFinishersModal] = useState(false);
  const autoAppendPopupCheckedRef = useRef(false);
  const [autoAppendAlreadyEnabled, setAutoAppendAlreadyEnabled] = useState(false);
  const didSyncChallengeProgressViewRef = useRef(false);

  // Responsive column count for masonry timeline (4 desktop, 2 mobile)
  const [snakeColCount, setSnakeColCount] = useState(window.innerWidth <= 480 ? 2 : 4);

  const tileUrlTemplateRef = useRef(null);
  const tileBasePathPrefixRef = useRef(null);
  const tileKeySetRef = useRef(new Set());
  const tileDataUrlRef = useRef(new Map());
  const tileLoadingModeRef = useRef(viewerOnly ? 'dynamic' : 'prefetch');
  // Guards the viewer-only autoplay so the flyover starts exactly once.
  const hasAutoStartedRef = useRef(false);
  const prefetchAbortRef = useRef(false);
  const tilesIndexReadyRef = useRef(false);
  const cacheBusterRef = useRef(0);
  const routeMapStyleChangesRef = useRef([]);
  const baseTerrainConfigRef = useRef(null);
  const basePlaceLabelColorsRef = useRef({});
  const placeLabelLayerIdsRef = useRef([]);
  const appliedRouteStyleIndexRef = useRef(null);
  const appliedRouteStyleSignatureRef = useRef(null);

  // DISABLED: terrain exaggeration & label color changes cause tile loading issues.
  // To re-enable, restore: sanitizeRouteMapStyleChanges(config?.display?.routeMapStyleChanges)
  const routeMapStyleChanges = useMemo(() => [], []);

  const getTileUrlTemplate = useCallback((basePath) => {
    const bucket = storage.app?.options?.storageBucket || 'mythic-maps.firebasestorage.app';
    const encodedBasePath = encodeURIComponent(`${basePath}/`);
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedBasePath}{z}_{x}_{y}.png?alt=media`;
  }, []);

  const lngToTileX = useCallback((lng, zoom) => {
    const n = Math.pow(2, zoom);
    return Math.floor(((lng + 180) / 360) * n);
  }, []);

  const latToTileY = useCallback((lat, zoom) => {
    const n = Math.pow(2, zoom);
    const latRad = (lat * Math.PI) / 180;
    return Math.floor(
      (1 - Math.log(Math.tan(latRad) + (1 / Math.cos(latRad))) / Math.PI) / 2 * n
    );
  }, []);

  const getTileKeyFromCoord = useCallback((coord, zoom) => {
    if (!coord) return null;
    const [lng, lat] = coord;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    const x = lngToTileX(lng, zoom);
    const y = latToTileY(lat, zoom);
    return `${zoom}/${x}/${y}`;
  }, [lngToTileX, latToTileY]);

  const getOrderedRouteTiles = useCallback((coordinates, zoom) => {
    const ordered = [];
    const seen = new Set();
    (coordinates || []).forEach((coord) => {
      const key = getTileKeyFromCoord(coord, zoom);
      if (!key || seen.has(key)) return;
      seen.add(key);
      ordered.push(key);
    });
    return ordered;
  }, [getTileKeyFromCoord]);

  const getNeighborTileKeys = useCallback((tileKey, radius = 1) => {
    if (!tileKey) return [];
    const [z, x, y] = tileKey.split('/').map(Number);
    if (!Number.isFinite(z) || !Number.isFinite(x) || !Number.isFinite(y)) return [];
    const keys = [];
    for (let dx = -radius; dx <= radius; dx += 1) {
      for (let dy = -radius; dy <= radius; dy += 1) {
        keys.push(`${z}/${x + dx}/${y + dy}`);
      }
    }
    return keys;
  }, []);

  const resolveAvailableTileKey = useCallback((z, x, y) => {
    const tileKeySet = tileKeySetRef.current;
    if (!tileKeySet || tileKeySet.size === 0) {
      return `${z}/${x}/${y}`;
    }

    const exactKey = `${z}/${x}/${y}`;
    if (tileKeySet.has(exactKey)) {
      return exactKey;
    }

    const zoomRange = tilesConfigRef.current?.tileZoomRange;
    const minZoom = Number.isFinite(zoomRange?.minZoom) ? zoomRange.minZoom : null;
    const maxZoom = Number.isFinite(zoomRange?.maxZoom) ? zoomRange.maxZoom : null;

    // Over-zoom fallback: if map requests z > max available, use parent tile.
    if (maxZoom !== null && z > maxZoom) {
      for (let targetZoom = maxZoom; targetZoom >= (minZoom ?? 0); targetZoom -= 1) {
        const dz = z - targetZoom;
        if (dz < 0) continue;
        const parentX = Math.floor(x / Math.pow(2, dz));
        const parentY = Math.floor(y / Math.pow(2, dz));
        const parentKey = `${targetZoom}/${parentX}/${parentY}`;
        if (tileKeySet.has(parentKey)) {
          return parentKey;
        }
      }
    }

    return null;
  }, []);

  const transparentTileUrlRef = useRef(null);

  const getTransparentTileUrl = useCallback(() => {
    if (transparentTileUrlRef.current) {
      return transparentTileUrlRef.current;
    }
    if (typeof document === 'undefined') {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 2;
    canvas.height = 2;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    const dataUrl = canvas.toDataURL('image/png');
    transparentTileUrlRef.current = dataUrl;
    return dataUrl;
  }, []);

  /**
   * Load all tile keys available under a basePath by listing storage.
   * Expects filenames like z_x_y.png.
   */
  const loadTileKeysFromBasePath = useCallback(async (basePath) => {
    const tileKeys = [];
    let pageToken = undefined;
    const filenameRegex = /^(\d+)_([\d-]+)_([\d-]+)\.(png|jpg|jpeg)$/i;

    do {
      const listRef = ref(storage, basePath);
      const result = await list(listRef, { maxResults: 1000, pageToken });
      result.items.forEach((itemRef) => {
        const name = itemRef.name;
        const match = name.match(filenameRegex);
        if (!match) return;
        const z = Number(match[1]);
        const x = Number(match[2]);
        const y = Number(match[3]);
        if (Number.isFinite(z) && Number.isFinite(x) && Number.isFinite(y)) {
          tileKeys.push(`${z}/${x}/${y}`);
        }
      });
      pageToken = result.nextPageToken;
    } while (pageToken);

    return tileKeys;
  }, []);

  // Track viewport width for masonry column count
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 480px)');
    const handler = (e) => setSnakeColCount(e.matches ? 2 : 4);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Load config from Firestore
  useEffect(() => {
    const loadConfig = async () => {
      if (!configId) {
        setError('No map ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const configRef = doc(db, 'gpxMapConfigs', configId);
        const configSnap = await getDoc(configRef);

        if (!configSnap.exists()) {
          setError('Map not found');
          setIsLoading(false);
          return;
        }

        const configData = configSnap.data();

        // Check if public or user is owner
        if (!configData.isPublic && configData.createdBy !== user?.uid) {
          setError('This map is private');
          setIsLoading(false);
          return;
        }

        // Reconstruct coordinates from flattened lngs/lats arrays
        if (configData.route?.lngs && configData.route?.lats) {
          configData.route.coordinates = configData.route.lngs.map((lng, i) => [lng, configData.route.lats[i]]);
        }

        setConfig(configData);
        setIsLoading(false);

        // Store tiles config for lazy loading (don't load all tiles upfront)
        if (configData.tiles?.basePath) {
          tilesConfigRef.current = configData.tiles;
          const template = getTileUrlTemplate(configData.tiles.basePath);
          tileUrlTemplateRef.current = template;
          tileBasePathPrefixRef.current = template.split('{z}')[0];
          tileDataUrlRef.current = new Map();
          tilesIndexReadyRef.current = false;

          // Always list all tiles in basePath (ignore stored tileKeys)
          try {
            const tileKeys = await loadTileKeysFromBasePath(configData.tiles.basePath);
            const zooms = tileKeys
              .map((key) => Number(key.split('/')[0]))
              .filter((z) => Number.isFinite(z));
            const minZoom = zooms.length ? Math.min(...zooms) : CUSTOM_TILE_ZOOM;
            const maxZoom = zooms.length ? Math.max(...zooms) : CUSTOM_TILE_ZOOM;
            tilesConfigRef.current = {
              ...tilesConfigRef.current,
              tileKeys,
              tileZoomRange: { minZoom, maxZoom }
            };
            tileKeySetRef.current = new Set(tileKeys);
            tilesIndexReadyRef.current = true;
            console.log(`[LAZY_TILES] Indexed ${tileKeys.length} tiles from ${configData.tiles.basePath}`);
            setTilesIndexVersion((v) => v + 1);
          } catch (err) {
            console.warn('[LAZY_TILES] Failed to index tiles from base path:', err);
          }
        }
      } catch (err) {
        console.error('Error loading config:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [configId, user, loadTileKeysFromBasePath]);

  // Update camera settings refs when config loads
  useEffect(() => {
    if (config?.display) {
      cameraAltitudeRef.current = config.display.cameraAltitude || DEFAULT_CAMERA_ALTITUDE;
      cameraDistanceRef.current = config.display.cameraDistance || DEFAULT_CAMERA_DISTANCE;
    }
  }, [config]);

  useEffect(() => {
    tileLoadingModeRef.current = tileLoadingMode;
  }, [tileLoadingMode]);

  useEffect(() => {
    routeMapStyleChangesRef.current = routeMapStyleChanges;
    appliedRouteStyleIndexRef.current = null;
    appliedRouteStyleSignatureRef.current = null;
    console.log('[ROUTE_STYLE][CHALLENGE] Loaded style changes:', routeMapStyleChanges);
  }, [routeMapStyleChanges]);

  const ensureTerrainSource = useCallback(() => {
    if (!map.current || !map.current.isStyleLoaded()) return false;
    if (!map.current.getSource('mapbox-dem')) {
      map.current.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
      });
    }
    return true;
  }, []);

  const getPlaceLabelLayerDetails = useCallback(() => {
    if (!map.current || !map.current.isStyleLoaded()) return [];
    const layers = map.current.getStyle().layers || [];
    const details = [];
    layers.forEach((layer) => {
      if (!isPlaceLabelLayer(layer)) return;
      const id = String(layer.id || '');
      const sourceLayer = String(layer['source-layer'] || '');
      let textColor;
      try {
        textColor = map.current.getPaintProperty(id, 'text-color');
      } catch (err) {
        textColor = undefined;
      }
      details.push({ id, sourceLayer, textColor });
    });
    return details;
  }, []);

  const captureRouteStyleBaselines = useCallback(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    const terrain = map.current.getTerrain?.();
    const styleTerrain = map.current.getStyle?.()?.terrain;
    const resolvedTerrain = terrain?.source ? terrain : (styleTerrain?.source ? styleTerrain : null);
    baseTerrainConfigRef.current = resolvedTerrain?.source
      ? {
        source: resolvedTerrain.source,
        ...(resolvedTerrain.exaggeration !== undefined ? { exaggeration: resolvedTerrain.exaggeration } : {})
      }
      : null;

    const debugLayers = getPlaceLabelLayerDetails();
    const placeLayerIds = debugLayers.map((layer) => layer.id);
    const baseColors = {};
    debugLayers.forEach((layer) => {
      if (layer.textColor !== undefined) {
        baseColors[layer.id] = layer.textColor;
      }
    });

    placeLabelLayerIdsRef.current = placeLayerIds;
    basePlaceLabelColorsRef.current = baseColors;
    appliedRouteStyleIndexRef.current = null;
    appliedRouteStyleSignatureRef.current = null;
    console.log('[ROUTE_STYLE][CHALLENGE] Captured baselines:', {
      terrainConfig: baseTerrainConfigRef.current,
      placeLabelLayerCount: placeLayerIds.length,
      placeLabelLayers: debugLayers
    });
  }, [getPlaceLabelLayerDetails]);

  const applyRouteMapStyleAtDistance = useCallback((distanceMeters) => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    const changes = routeMapStyleChangesRef.current || [];
    let activeIndex = -1;
    for (let i = 0; i < changes.length; i += 1) {
      if (distanceMeters >= changes[i].distanceMeters) {
        activeIndex = i;
      } else {
        break;
      }
    }

    const active = activeIndex >= 0 ? changes[activeIndex] : null;
    const activeSignature = active
      ? `${activeIndex}:${active.distanceMeters}:${active.terrainExaggeration}:${active.placeLabelColor}`
      : 'baseline';

    const trackedLoadedLayerIds = placeLabelLayerIdsRef.current.filter((layerId) => map.current.getLayer(layerId));
    const shouldRefreshLayerDiscovery = activeIndex >= 0 && trackedLoadedLayerIds.length === 0;

    if (
      appliedRouteStyleIndexRef.current === activeIndex
      && appliedRouteStyleSignatureRef.current === activeSignature
      && !shouldRefreshLayerDiscovery
    ) {
      return;
    }

    const prevIndex = appliedRouteStyleIndexRef.current;
    const prevSignature = appliedRouteStyleSignatureRef.current;
    appliedRouteStyleIndexRef.current = activeIndex;
    appliedRouteStyleSignatureRef.current = activeSignature;

    console.log('[ROUTE_STYLE][CHALLENGE] Transition:', {
      fromIndex: prevIndex,
      toIndex: activeIndex,
      distanceMeters: Math.round(distanceMeters),
      fromSignature: prevSignature,
      toSignature: activeSignature
    });

    if (activeIndex >= 0) {
      if (shouldRefreshLayerDiscovery) {
        const refreshed = getPlaceLabelLayerDetails();
        if (refreshed.length > 0) {
          placeLabelLayerIdsRef.current = refreshed.map((layer) => layer.id);
          refreshed.forEach((layer) => {
            if (basePlaceLabelColorsRef.current[layer.id] === undefined && layer.textColor !== undefined) {
              basePlaceLabelColorsRef.current[layer.id] = layer.textColor;
            }
          });
          console.log('[ROUTE_STYLE][CHALLENGE] Refreshed label layers for dynamic update:', refreshed.length);
        }
      }

      if (ensureTerrainSource()) {
        map.current.setTerrain({
          source: 'mapbox-dem',
          exaggeration: active.terrainExaggeration
        });
      }

      const targetLayerIds = placeLabelLayerIdsRef.current.filter((layerId) => map.current.getLayer(layerId));
      if (targetLayerIds.length === 0) {
        console.warn('[ROUTE_STYLE][CHALLENGE] No place label layers available for color update');
      }

      targetLayerIds.forEach((layerId) => {
        if (!map.current.getLayer(layerId)) return;
        try {
          map.current.setPaintProperty(layerId, 'text-color', active.placeLabelColor);
        } catch (err) {
          // Ignore layers that don't support text-color updates.
        }
      });
      return;
    }

    let baselineTerrain = baseTerrainConfigRef.current;
    if (!baselineTerrain?.source) {
      const styleTerrain = map.current.getStyle?.()?.terrain;
      if (styleTerrain?.source) {
        baselineTerrain = {
          source: styleTerrain.source,
          ...(styleTerrain.exaggeration !== undefined ? { exaggeration: styleTerrain.exaggeration } : {})
        };
        baseTerrainConfigRef.current = baselineTerrain;
      }
    }

    if (baselineTerrain?.source) {
      if (baselineTerrain.source === 'mapbox-dem') {
        ensureTerrainSource();
      }
      try {
        map.current.setTerrain(baselineTerrain);
      } catch (err) {
        console.warn('[ROUTE_STYLE][CHALLENGE] Failed to restore baseline terrain:', err);
      }
    } else if (prevIndex !== null && prevIndex >= 0 && map.current.getTerrain?.()) {
      try {
        map.current.setTerrain(null);
      } catch (err) {
        console.warn('[ROUTE_STYLE][CHALLENGE] Failed to clear terrain:', err);
      }
    }

    const resetLayerIds = placeLabelLayerIdsRef.current.filter((layerId) => map.current.getLayer(layerId));
    resetLayerIds.forEach((layerId) => {
      if (!map.current.getLayer(layerId)) return;
      const baseColor = basePlaceLabelColorsRef.current[layerId];
      try {
        if (baseColor === undefined) {
          map.current.setPaintProperty(layerId, 'text-color', null);
        } else {
          map.current.setPaintProperty(layerId, 'text-color', baseColor);
        }
      } catch (err) {
        // Ignore layers that don't support text-color updates.
      }
    });
  }, [ensureTerrainSource, getPlaceLabelLayerDetails]);

  const prefetchTile = useCallback(async (tileKey) => {
    if (!tileKey) return;
    if (tileDataUrlRef.current.has(tileKey)) return;
    const template = tileUrlTemplateRef.current;
    if (!template) return;

    const [z, x, y] = tileKey.split('/').map(Number);
    if (!Number.isFinite(z) || !Number.isFinite(x) || !Number.isFinite(y)) return;
    let url = template
      .replace('{z}', z)
      .replace('{x}', x)
      .replace('{y}', y);

    // Add cache buster if set
    if (cacheBusterRef.current > 0) {
      url += `&_cb=${cacheBusterRef.current}`;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      if (typeof dataUrl === 'string') {
        tileDataUrlRef.current.set(tileKey, dataUrl);
      }
    } catch (err) {
      // Silent for background prefetch
    }
  }, []);

  const startTilePrefetch = useCallback(() => {
    if (tileLoadingMode !== 'prefetch') return;
    const tilesConfig = tilesConfigRef.current;
    const tileKeys = tilesConfig?.tileKeys || [];
    const tileZoomRange = tilesConfig?.tileZoomRange;
    if (!tileKeys.length || !config?.route?.coordinates?.length) return;

    const tileKeySet = tileKeySetRef.current || new Set();
    const tileZoom = tilesConfig?.tileZoomRange?.maxZoom ?? CUSTOM_TILE_ZOOM;
    const orderedRouteTiles = getOrderedRouteTiles(config.route.coordinates, tileZoom)
      .filter((key) => tileKeySet.has(key));

    const startTile = orderedRouteTiles[0];
    const priorityKeys = [
      ...getNeighborTileKeys(startTile, 1),
      ...orderedRouteTiles.slice(0, 12)
    ].filter((key, idx, arr) => key && arr.indexOf(key) === idx && tileKeySet.has(key));

    const prioritySet = new Set(priorityKeys);
    const remainingKeys = tileKeys.filter((key) => !prioritySet.has(key));
    const queue = [...priorityKeys, ...remainingKeys];

    // Count already cached tiles
    const alreadyCached = queue.filter(key => tileDataUrlRef.current.has(key)).length;
    let loadedCount = alreadyCached;
    setTileProgress({ loaded: loadedCount, total: queue.length });

    prefetchAbortRef.current = false;
    const concurrency = 6;
    let index = 0;

    const worker = async () => {
      while (!prefetchAbortRef.current && index < queue.length) {
        const current = queue[index];
        index += 1;
        const wasCached = tileDataUrlRef.current.has(current);
        await prefetchTile(current);
        if (!wasCached && tileDataUrlRef.current.has(current)) {
          loadedCount += 1;
          setTileProgress(prev => ({ ...prev, loaded: loadedCount }));
        }
      }
    };

    for (let i = 0; i < concurrency; i += 1) {
      worker();
    }
  }, [tileLoadingMode, config, getOrderedRouteTiles, getNeighborTileKeys, prefetchTile]);

  useEffect(() => {
    prefetchAbortRef.current = true;
    if (tileLoadingMode === 'prefetch') {
      startTilePrefetch();
    }
    return () => {
      prefetchAbortRef.current = true;
    };
  }, [tileLoadingMode, tilesIndexVersion, startTilePrefetch]);

  useEffect(() => {
    localStorage.setItem('gpxMap_tileLoadingMode', tileLoadingMode);
  }, [tileLoadingMode]);

  const clearTileCache = useCallback(() => {
    // Abort any ongoing prefetch
    prefetchAbortRef.current = true;
    // Clear the cached tile data URLs
    tileDataUrlRef.current.clear();
    // Increment cache buster to bypass browser disk cache
    cacheBusterRef.current = Date.now();
    // Reset progress
    setTileProgress({ loaded: 0, total: 0 });
    // Restart prefetch if in prefetch mode
    if (tileLoadingMode === 'prefetch') {
      setTimeout(() => {
        startTilePrefetch();
      }, 100);
    }
    console.log('[TILES] Cache cleared with buster:', cacheBusterRef.current);
  }, [tileLoadingMode, startTilePrefetch]);

  // Compute checkpoint distances along the route (memoized)
  const checkpointDistances = useMemo(() => {
    if (!config?.route?.coordinates || !config?.route?.checkpoints?.length) return [];
    const coordinates = config.route.coordinates;
    const cumDist = calculateCumulativeDistances(coordinates);
    return config.route.checkpoints.map(cp =>
      findCheckpointDistanceOnRoute(coordinates, cumDist, cp) ?? 0
    );
  }, [config]);

  // Get current user's progress from participants list
  const userProgress = useMemo(() => {
    if (!user?.uid || !participants.length) {
      return participantData?.currentDistanceMeters || 0;
    }
    const me = participants.find(p => p.oddc === user.uid);
    return me?.currentDistanceMeters ?? participantData?.currentDistanceMeters ?? 0;
  }, [user, participants, participantData]);

  // Fog of war: max distance viewable on the map (start to first real checkpoint is always visible)
  const maxViewableDistance = useMemo(() => {
    if (!config?.isChallenge) return Infinity;
    // Embedded "Aha" viewer: reveal/stop at exactly the rider's projected
    // distance so the flyover travels their real last-activity distance and the
    // puck stops where they would land. This single clamp drives the fog trim,
    // the checkpoint popup gating, marker visibility, and the animation end.
    if (viewerOnly && Number.isFinite(projectedDistanceMeters) && projectedDistanceMeters > 0) {
      return projectedDistanceMeters;
    }
    // Find first checkpoint with meaningful distance (skip start marker at ~0m)
    const firstRealCpDistance = checkpointDistances.find(d => d > 100) || 0;
    return Math.max(firstRealCpDistance, userProgress) || Infinity;
  }, [config?.isChallenge, checkpointDistances, userProgress, viewerOnly, projectedDistanceMeters]);

  const maxViewableDistanceRef = useRef(Infinity);
  maxViewableDistanceRef.current = maxViewableDistance;

  // Diamonds earned from checkpoints: the backend awards 1 per checkpoint and 2 for
  // the final one (see challenge_handler.ts), so total possible = checkpoints + 1.
  const diamondStats = useMemo(() => {
    const totalCheckpoints = config?.route?.checkpoints?.length || 0;
    const totalPossible = totalCheckpoints > 0 ? totalCheckpoints + 1 : 0;
    let earned = 0;
    rewardedCheckpoints.forEach(idx => {
      earned += (idx === totalCheckpoints - 1) ? 2 : 1;
    });
    return { earned, totalPossible };
  }, [config, rewardedCheckpoints]);

  // User completion and personal stats
  const totalRouteDistanceM = getChallengeTotalDistanceMeters(config?.route);
  const isCompleted = hasJoined &&
    isChallengeParticipantComplete(participantData, userProgress, config?.route);

  // Scroll to travelog section when URL contains #travelog hash (e.g. from email CTA)
  useEffect(() => {
    if (viewerOnly) return;
    if (location.hash === '#travelog' && journeySectionRef.current) {
      // Small delay to let the layout settle after data loads
      const timer = setTimeout(() => {
        journeySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.hash, journeyLoaded, viewerOnly]);

  // Callback ref: fires confetti when completed banner mounts and scrolls into view
  const completedBannerRef = useCallback(node => {
    if (node && isCompleted && !confettiFiredRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !confettiFiredRef.current) {
            confettiFiredRef.current = true;
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 3000);
            posthog?.capture('challenge_completed', {
              page: 'challenge_detail',
              challenge_id: configId,
              user_progress_meters: participantData?.currentDistanceMeters || 0,
              activity_count: participantData?.activities?.length || 0,
              checkpoints_reached: participantData?.checkpointsReached?.length || 0,
            });
            observer.disconnect();
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(node);
    }
  }, [isCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Callback ref: fires confetti when Kyoto bookend scrolls into view (completed users only)
  const kyotoBookendRef = useCallback(node => {
    if (node && isCompleted && !kyotoConfettiFiredRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !kyotoConfettiFiredRef.current) {
            kyotoConfettiFiredRef.current = true;
            setShowKyotoConfetti(true);
            setTimeout(() => setShowKyotoConfetti(false), 3000);
            observer.disconnect();
          }
        },
        { threshold: 0.5 }
      );
      observer.observe(node);
    }
  }, [isCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const userStats = useMemo(() => {
    if (!hasJoined || !participantData) return null;

    const joinedAt = participantData.joinedAt?.toDate ? participantData.joinedAt.toDate() : null;
    const daysPassed = joinedAt ? Math.max(1, Math.floor((Date.now() - joinedAt.getTime()) / (1000 * 60 * 60 * 24))) : null;
    const remainingKm = Math.max(0, ((totalRouteDistanceM - userProgress) / 1000)).toFixed(1);
    const activityCount = (participantData.activities || []).length;
    const me = participants.find(p => p.oddc === user?.uid);
    const elevationGain = Math.round(me?.totalElevationGain || 0);

    // Finished date: last activity by cumulative distance
    let finishedDate = null;
    if (isCompleted && participantData.activities?.length) {
      const sorted = [...participantData.activities].sort(
        (a, b) => (b.cumulativeDistance || 0) - (a.cumulativeDistance || 0)
      );
      const lastRaw = sorted[0]?.activityDate;
      if (lastRaw?.toDate) finishedDate = lastRaw.toDate();
      else if (lastRaw) finishedDate = new Date(lastRaw);
    }

    const formatDate = (d) => {
      if (!d) return '—';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    return {
      startedStr: formatDate(joinedAt),
      finishedStr: formatDate(finishedDate),
      remainingKm,
      daysPassed,
      activityCount,
      checkpointCount: rewardedCheckpoints.length,
      elevationGain,
      diamondsEarned: diamondStats.earned,
    };
  }, [hasJoined, participantData, totalRouteDistanceM, userProgress, participants, user, isCompleted, rewardedCheckpoints, diamondStats]);

  // Travelog owner info (name + photo) for personalized title
  const travelogOwner = useMemo(() => {
    if (!user?.uid) return { name: null, photoURL: null };
    const me = participants.find(p => p.oddc === user.uid);
    return {
      name: me?.displayName || user.displayName || null,
      photoURL: me?.photoURL || user.photoURL || null,
    };
  }, [user, participants]);

  // Export travelog as PNG image using html2canvas
  const handlePrintCertificate = useCallback(async () => {
    const src = journeySectionRef.current;
    if (!src || isExporting) return;

    setIsExporting(true);

    // Load profile photo as data URL via <img> to avoid CORS issues with fetch()
    let profilePhotoDataUrl = null;
    if (travelogOwner.photoURL) {
      try {
        profilePhotoDataUrl = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          };
          img.onerror = () => reject(new Error('Failed to load profile image'));
          img.src = travelogOwner.photoURL;
        });
      } catch (err) {
        console.warn('Could not load profile photo for export:', err.message);
      }
    }

    // Track elements we temporarily hide so we can restore them
    const hideSelectors = ['.travelog-print-btn', '.confetti-container', '.tl-story-create-btn'];
    if (!profilePhotoDataUrl) {
      hideSelectors.push('.travelog-profile-pic');
    }
    const hiddenElements = [];
    let logoOriginalParent = null;
    let logoNextSibling = null;

    try {
      // Temporarily hide unwanted elements
      hideSelectors.forEach(sel => {
        src.querySelectorAll(sel).forEach(el => {
          hiddenElements.push({ el, prev: el.style.display });
          el.style.display = 'none';
        });
      });

      // Show the print-only logo and move it above everything
      const logo = src.querySelector('.travelog-print-logo');
      if (logo) {
        logoOriginalParent = logo.parentNode;
        logoNextSibling = logo.nextSibling;
        logo.style.display = 'flex';
        src.insertBefore(logo, src.firstChild);
      }

      // Capture the DOM section as a canvas
      const canvas = await html2canvas(src, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: challengeSurfaces.bgSecondary,
        scale: 2,
        logging: false,
        imageTimeout: 15000,
        onclone: (_clonedDoc, clonedElement) => {
          // Ensure CSS custom properties are set (they may not transfer to the clone's iframe).
          // Page-scoped --gpx-* tokens come from the active challenge so exported
          // PNGs match what's on-screen. Accent tokens follow the same pattern.
          const vars = {
            // Surfaces (challenge-scoped)
            '--gpx-bg-primary': challengeSurfaces.bgPrimary,
            '--gpx-bg-secondary': challengeSurfaces.bgSecondary,
            '--gpx-bg-tertiary': challengeSurfaces.bgTertiary,
            '--gpx-bg-card': challengeSurfaces.bgCard,
            '--gpx-border-primary': challengeSurfaces.borderPrimary,
            '--gpx-border-secondary': challengeSurfaces.borderSecondary,
            '--gpx-bg-locked': challengeSurfaces.bgLocked,
            // Accents (challenge-scoped)
            '--challenge-primary': challengeTheme.primary,
            '--challenge-primary-dark': challengeTheme.primaryDark,
            '--challenge-secondary': challengeTheme.secondary,
            '--challenge-tint': challengeTheme.tint,
            '--challenge-on-primary': challengeTheme.onPrimary,
            // Text + functional tokens (legacy app values — html2canvas needs literals
            // because the clone's iframe doesn't inherit :root variables reliably).
            '--text-primary': '#2c2c2c',
            '--text-secondary': '#4a4a4a',
            '--text-muted': '#8a8078',
            '--shadow-sm': '0 1px 3px rgba(0,0,0,0.05)',
            '--shadow-md': '0 2px 8px rgba(0,0,0,0.08)',
          };
          Object.entries(vars).forEach(([k, v]) => clonedElement.style.setProperty(k, v));

          // Replace profile pic src with pre-fetched data URL, or fall back to letter avatar
          const pic = clonedElement.querySelector('.travelog-profile-pic');
          if (pic) {
            if (profilePhotoDataUrl) {
              pic.src = profilePhotoDataUrl;
              pic.style.display = '';
            } else if (travelogOwner.name) {
              pic.style.display = '';
              const size = 80;
              const c = document.createElement('canvas');
              c.width = size;
              c.height = size;
              const ctx = c.getContext('2d');
              ctx.fillStyle = '#c4956a';
              ctx.beginPath();
              ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
              ctx.fill();
              ctx.fillStyle = 'white';
              ctx.font = `bold ${size * 0.45}px Inter, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(travelogOwner.name[0].toUpperCase(), size / 2, size / 2);
              pic.src = c.toDataURL();
            }
          }

          // Force lazy images to load eagerly in the clone
          clonedElement.querySelectorAll('img[loading="lazy"]').forEach(img => {
            img.removeAttribute('loading');
          });

          // Ensure masonry overflow is visible (connector lines extend into margins)
          const masonry = clonedElement.querySelector('.travelog-masonry');
          if (masonry) masonry.style.overflow = 'visible';
        },
      });

      // Trigger PNG download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${challengeCatalogEntry?.slug || 'mythic'}-travelog.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        posthog?.capture('challenge_travelog_exported', {
          page: 'challenge_detail',
          challenge_id: configId,
          is_completed: isCompleted,
        });
      }, 'image/png');

    } catch (error) {
      console.error('Error exporting travelog image:', error);
    } finally {
      // Restore all hidden elements
      hiddenElements.forEach(({ el, prev }) => {
        el.style.display = prev;
      });

      // Move logo back to its original position and re-hide it
      const logo = src.querySelector('.travelog-print-logo');
      if (logo) {
        logo.style.display = 'none';
        if (logoOriginalParent) {
          if (logoNextSibling) {
            logoOriginalParent.insertBefore(logo, logoNextSibling);
          } else {
            logoOriginalParent.appendChild(logo);
          }
        }
      }

      setIsExporting(false);
    }
  }, [isExporting, travelogOwner.name, travelogOwner.photoURL, challengeSurfaces, challengeTheme, challengeCatalogEntry]);

  // Aggregate challenge stats from all participants (server-computed fields).
  // On cold-start challenges (no participants yet) we still return zeroed
  // totals so the stats strip renders — empty but present, signalling "be
  // the first" rather than vanishing the whole section.
  const challengeStats = useMemo(() => {
    if (!participants.length) {
      return {
        athletes: 0,
        distance: 0,
        movingTime: 0,
        elevationGain: 0,
        activities: 0,
        checkpointsVisited: 0,
      };
    }

    let totalDistance = 0;
    let totalMovingTime = 0;
    let totalElevationGain = 0;
    let totalActivities = 0;
    let totalCheckpointsVisited = 0;

    participants.forEach(p => {
      totalDistance += p.currentDistanceMeters || 0;
      totalMovingTime += p.totalMovingTime || 0;
      totalElevationGain += p.totalElevationGain || 0;
      totalActivities += (p.activities || []).length;

      if (checkpointDistances.length > 0) {
        const progress = p.currentDistanceMeters || 0;
        checkpointDistances.forEach(cpDist => {
          if (progress >= cpDist) totalCheckpointsVisited++;
        });
      }
    });

    return {
      athletes: participants.length,
      distance: totalDistance,
      movingTime: totalMovingTime,
      elevationGain: totalElevationGain,
      activities: totalActivities,
      checkpointsVisited: totalCheckpointsVisited,
    };
  }, [participants, checkpointDistances]);

  // Compute finishers data for the leaderboard indicator and modal
  const finishersData = useMemo(() => {
    const routeDistM = totalRouteDistanceM;
    if (!routeDistM || !participants.length) {
      return { finishers: [], nonFinishers: participants, count: 0, firstFive: [], lastFive: [], userRank: null };
    }

    // Use 99% threshold — cumulative activity distance may not exactly reach the route stat total
    const finishThreshold = routeDistM * 0.99;
    const finishers = [];
    const nonFinishers = [];

    participants.forEach(p => {
      const participantProgress = p.currentDistanceMeters || 0;
      const isFinisher = isChallengeParticipantComplete(p, participantProgress, config?.route) ||
        participantProgress >= finishThreshold;

      if (isFinisher) {
        // Derive finish date from last activity by cumulativeDistance
        let finishDate = null;
        if (p.activities?.length) {
          const sorted = [...p.activities].sort(
            (a, b) => (b.cumulativeDistance || 0) - (a.cumulativeDistance || 0)
          );
          const lastRaw = sorted[0]?.activityDate;
          if (lastRaw?.toDate) finishDate = lastRaw.toDate();
          else if (lastRaw) finishDate = new Date(lastRaw);
        }
        finishers.push({ ...p, finishDate });
      } else {
        nonFinishers.push(p);
      }
    });

    // Sort by finish date ascending (earliest first)
    finishers.sort((a, b) => {
      if (!a.finishDate && !b.finishDate) return 0;
      if (!a.finishDate) return 1;
      if (!b.finishDate) return -1;
      return a.finishDate.getTime() - b.finishDate.getTime();
    });

    const firstFive = finishers.slice(0, 5);
    const lastFive = [...finishers].reverse().slice(0, 5);
    const userRank = user?.uid
      ? (finishers.findIndex(f => f.oddc === user.uid) + 1) || null
      : null;

    return { finishers, nonFinishers, count: finishers.length, firstFive, lastFive, userRank };
  }, [participants, totalRouteDistanceM, config?.route, user?.uid]);

  // Generate SVG path for elevation profile behind leaderboard track
  const elevationPathD = useMemo(() => {
    const elevations = config?.route?.elevations;
    if (!elevations || elevations.length < 2) return null;

    // Downsample to ~200 points
    const targetPoints = 200;
    const stride = Math.max(1, Math.floor(elevations.length / targetPoints));
    const sampled = [];
    for (let i = 0; i < elevations.length; i += stride) {
      const val = elevations[i];
      if (val != null && !isNaN(val)) sampled.push(val);
    }
    // Always include last point
    const lastVal = elevations[elevations.length - 1];
    if (lastVal != null && !isNaN(lastVal) && sampled.length > 0) {
      sampled.push(lastVal);
    }
    if (sampled.length < 2) return null;

    const min = Math.min(...sampled);
    const max = Math.max(...sampled);
    const range = max - min;

    // Build area-fill path in viewBox 0 0 1000 100
    const points = sampled.map((ele, i) => {
      const x = (i / (sampled.length - 1)) * 1000;
      const y = range === 0
        ? 50  // flat route fallback
        : 100 - ((ele - min) / range) * 85;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });

    return `M 0,100 L ${points.join(' L ')} L 1000,100 Z`;
  }, [config]);

  // Compute activity segments with start/end/midpoint distances for map popups & route gradient
  const activitySegments = useMemo(() => {
    if (!hasJoined || !participantData?.activities?.length) return [];

    const journeyMap = new Map();
    journeyActivities.forEach(ja => journeyMap.set(String(ja.id), ja));

    const sorted = [...participantData.activities].sort(
      (a, b) => (a.cumulativeDistance || 0) - (b.cumulativeDistance || 0)
    );

    return sorted.map((raw) => {
      const end = raw.cumulativeDistance || 0;
      const dist = raw.distanceMeters || 0;
      const start = end - dist;
      const enriched = journeyMap.get(String(raw.activityId)) || null;
      return {
        start,
        end,
        midpoint: (start + end) / 2,
        activityId: raw.activityId,
        activityDate: raw.activityDate,
        distanceMeters: dist,
        enrichedActivity: enriched,
      };
    });
  }, [hasJoined, participantData, journeyActivities]);

  // Sync activity distance ranges to ref for use in animation closures
  useEffect(() => {
    activityDistanceRangesRef.current = activitySegments.map(s => ({
      start: s.start, end: s.end, midpoint: s.midpoint
    }));
  }, [activitySegments]);

  // Build unified timeline events merging checkpoints and activities, sorted by distance
  const timelineEvents = useMemo(() => {
    const events = [];

    // Add checkpoint events
    if (config?.route?.checkpoints?.length && checkpointDistances.length) {
      config.route.checkpoints.forEach((cp, idx) => {
        const cpDistance = checkpointDistances[idx] || 0;
        const isUnlocked = hasJoined && userProgress >= cpDistance;
        const isLast = idx === config.route.checkpoints.length - 1;
        const act = getActForCheckpointIndex(challengeActs, idx);
        const athletesReached = participants.filter(p => (p.currentDistanceMeters || 0) >= cpDistance).length;
        events.push({
          type: 'checkpoint',
          distance: cpDistance,
          globalIdx: idx,
          checkpoint: cp,
          isUnlocked,
          isLast,
          act,
          athletesReached,
        });
      });
    }

    // Add activity events from participantData (available before enriched data loads)
    if (hasJoined && participantData?.activities?.length) {
      const journeyMap = new Map();
      journeyActivities.forEach(ja => {
        journeyMap.set(String(ja.id), ja);
      });

      participantData.activities.forEach((rawActivity) => {
        const enriched = journeyMap.get(String(rawActivity.activityId));
        const existingGeneration = activityGenerationMap[String(rawActivity.activityId)] || null;
        events.push({
          type: 'activity',
          distance: rawActivity.cumulativeDistance || 0,
          distanceMeters: rawActivity.distanceMeters || 0,
          activityDate: rawActivity.activityDate,
          activityId: rawActivity.activityId,
          enrichedActivity: enriched || null,
          isLoaded: !!enriched,
          generatedMapUrl: existingGeneration?.generatedImageUrl || null,
          generatedMapRequestId: existingGeneration?.generationId || null,
        });
      });
    }

    // Add "you are here" marker as a special event until the route is complete.
    // Once completed, Kyoto becomes the active endpoint instead.
    if (hasJoined && userProgress > 0 && !isCompleted) {
      events.push({
        type: 'progress',
        distance: userProgress,
      });
    }

    // Sort by distance ascending, with progress marker after items at same distance
    events.sort((a, b) => {
      if (a.distance !== b.distance) return a.distance - b.distance;
      // At same distance: checkpoints first, then activities, then progress
      const order = { checkpoint: 0, activity: 1, progress: 2 };
      return (order[a.type] || 0) - (order[b.type] || 0);
    });

    // Annotate act transitions on checkpoint events
    let currentActName = null;
    events.forEach(event => {
      if (event.type === 'checkpoint') {
        const actName = event.act?.name;
        if (actName !== currentActName) {
          event.isActStart = true;
          currentActName = actName;
        }
      }
    });

    return events;
  }, [config, checkpointDistances, hasJoined, userProgress, isCompleted, participantData,
    journeyActivities, rewardedCheckpoints, participants, activityGenerationMap]);

  // Group timeline events into snake rows for masonry layout
  const snakeRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < timelineEvents.length; i += snakeColCount) {
      const chunk = timelineEvents.slice(i, i + snakeColCount);
      const rowIdx = Math.floor(i / snakeColCount);
      const direction = rowIdx % 2 === 0 ? 'ltr' : 'rtl';
      // Reverse events for RTL rows so DOM order matches visual left-to-right
      rows.push({ events: direction === 'rtl' ? [...chunk].reverse() : chunk, direction, rowIdx });
    }
    return rows;
  }, [timelineEvents, snakeColCount]);

  // Sync checkpointDistances to ref for use in animation closures
  useEffect(() => {
    checkpointDistancesRef.current = checkpointDistances;
  }, [checkpointDistances]);

  useEffect(() => {
    didSyncChallengeProgressViewRef.current = false;
  }, [configId, user?.uid]);

  // Check if user has joined the challenge and fetch participant data
  useEffect(() => {
    if (!config?.isChallenge || !user?.uid || !configId) return;

    const checkParticipation = async () => {
      try {
        const participantRef = doc(db, 'gpxMapConfigs', configId, 'participants', user.uid);
        const participantSnap = await getDoc(participantRef);
        setHasJoined(participantSnap.exists());
        if (participantSnap.exists()) {
          setParticipantData(participantSnap.data());
        }
      } catch (err) {
        console.error('[CHALLENGE] Error checking participation:', err);
      }
    };
    checkParticipation();
  }, [config, user, configId]);

  // Fetch checkpoint diamond rewards for this challenge
  useEffect(() => {
    if (!config?.isChallenge || !user?.uid || !configId || !hasJoined) return;

    const fetchRewards = async () => {
      try {
        const rewardsRef = doc(db, 'users', user.uid, 'creditRewards', `challenge_${configId}`);
        const rewardsSnap = await getDoc(rewardsRef);
        if (rewardsSnap.exists()) {
          setRewardedCheckpoints(rewardsSnap.data()?.rewardedCheckpoints || []);
        }
      } catch (err) {
        console.error('[CHALLENGE] Error fetching checkpoint rewards:', err);
      }
    };
    fetchRewards();
  }, [config, user, configId, hasJoined]);

  // Listen to participants for real-time updates
  useEffect(() => {
    if (viewerOnly) return; // embedded viewer has no leaderboard; skip the listener
    if (!config?.isChallenge || !configId) return;

    console.log('[CHALLENGE] Setting up participants listener for config:', configId);
    console.log('[CHALLENGE] Config isPublic:', config?.isPublic, 'isChallenge:', config?.isChallenge);

    const unsubscribe = onSnapshot(
      collection(db, 'gpxMapConfigs', configId, 'participants'),
      (snapshot) => {
        console.log('[CHALLENGE] Received participants snapshot:', snapshot.docs.length, 'participants');
        const participantsList = snapshot.docs.map(doc => ({
          oddc: doc.id,
          ...doc.data()
        }));
        // Sort by progress (descending) for leaderboard
        participantsList.sort((a, b) =>
          (b.currentDistanceMeters || 0) - (a.currentDistanceMeters || 0)
        );
        setParticipants(participantsList);

        if (user?.uid) {
          const currentParticipant = participantsList.find((participant) => participant.oddc === user.uid) || null;
          setHasJoined(!!currentParticipant);
          setParticipantData(currentParticipant);
        }
      },
      (error) => {
        console.error('[CHALLENGE] Error in participants listener:', error);
        console.error('[CHALLENGE] Error code:', error.code);
        console.error('[CHALLENGE] Config details - isPublic:', config?.isPublic, 'isChallenge:', config?.isChallenge);
      }
    );

    return () => unsubscribe();
  }, [configId, config?.isChallenge, config?.isPublic, user?.uid, viewerOnly]);

  // Update route color split based on user progress (challenge mode)
  const updateRouteProgress = useCallback((progressMeters) => {
    if (!map.current || !cumulativeDistancesRef.current) return;
    if (!map.current.getLayer('route-line')) return;
    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];
    if (!totalDistance) return;
    const fraction = Math.max(0, Math.min(1, progressMeters / totalDistance));
    map.current.setPaintProperty('route-line', 'line-trim-offset', [fraction, 1.0]);

    // Fog of war: hide route outline beyond maxViewableDistance
    if (map.current.getLayer('route-outline')) {
      const maxView = maxViewableDistanceRef.current;
      const fogFraction = (maxView > 0 && maxView < Infinity) ? Math.max(0, Math.min(1, maxView / totalDistance)) : 1;
      map.current.setPaintProperty('route-outline', 'line-trim-offset', [fogFraction, 1.0]);
      map.current.setPaintProperty('route-outline', 'line-trim-color', 'rgba(0,0,0,0)');
    }
  }, []);

  useEffect(() => {
    if (!mapReady || !config?.isChallenge) return;
    updateRouteProgress(userProgress);
  }, [userProgress, config?.isChallenge, mapReady, updateRouteProgress]);

  // Update route line gradient to highlight current activity segment
  const updateRouteGradient = useCallback((cameraDistance) => {
    if (!map.current || !cumulativeDistancesRef.current || !map.current.getLayer('route-line')) return;

    const ranges = activityDistanceRangesRef.current;
    if (!ranges.length) return;

    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];
    if (!totalDistance) return;

    // Find which activity the puck is inside
    let currentIdx = -1;
    for (let i = 0; i < ranges.length; i++) {
      if (cameraDistance >= ranges[i].start && cameraDistance <= ranges[i].end) {
        currentIdx = i;
        break;
      }
    }

    // Only update gradient when the current activity changes
    if (currentIdx === currentActivityIdxRef.current) return;
    currentActivityIdxRef.current = currentIdx;

    // Build gradient stops: collect all boundary points, then create stops array
    // Each stop is [fraction, color]. Use tight epsilon for sharp transitions.
    const EPS = 0.0001;
    const stopPairs = []; // array of { frac, color }
    const colorCurrent = routeColorsRef.current.current;
    const colorOther = routeColorsRef.current.other;

    // Start at 0 with "other" color (covers gap before first activity)
    stopPairs.push({ frac: 0, color: colorOther });

    for (let i = 0; i < ranges.length; i++) {
      const startFrac = Math.max(0, ranges[i].start / totalDistance);
      const endFrac = Math.min(1, ranges[i].end / totalDistance);
      const color = (i === currentIdx) ? colorCurrent : colorOther;

      // Sharp transition into this activity
      stopPairs.push({ frac: Math.max(startFrac - EPS, 0), color: colorOther });
      stopPairs.push({ frac: startFrac, color });
      stopPairs.push({ frac: endFrac, color });
      // Sharp transition out of this activity
      stopPairs.push({ frac: Math.min(endFrac + EPS, 1), color: colorOther });
    }

    // End at 1 with "other" color
    stopPairs.push({ frac: 1, color: colorOther });

    // Deduplicate and ensure strictly monotonic fractions
    const stops = [];
    let lastFrac = -1;
    for (const { frac, color } of stopPairs) {
      const safeFrac = Math.max(frac, lastFrac + EPS / 10);
      if (safeFrac > 1) break;
      stops.push(safeFrac, color);
      lastFrac = safeFrac;
    }

    // Ensure we end at 1
    if (stops.length >= 2 && stops[stops.length - 2] < 1) {
      stops.push(1, colorOther);
    }

    const gradientExpr = ['interpolate', ['linear'], ['line-progress'], ...stops];
    map.current.setPaintProperty('route-line', 'line-gradient', gradientExpr);
  }, []);

  // Set initial route gradient when activity segments become available
  useEffect(() => {
    if (!mapReady || !activitySegments.length) return;
    // Force re-compute by invalidating the cached index
    currentActivityIdxRef.current = -999;
    updateRouteGradient(currentDistanceRef.current || 0);
  }, [mapReady, activitySegments, updateRouteGradient]);

  // Render participant markers on map
  useEffect(() => {
    if (!mapReady || !map.current || !cumulativeDistancesRef.current || !config?.route?.coordinates || !config?.isChallenge) return;

    const coordinates = config.route.coordinates;
    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];

    // Remove markers for participants who left
    Object.keys(participantMarkersRef.current).forEach(oddc => {
      if (!participants.find(p => p.oddc === oddc)) {
        participantMarkersRef.current[oddc].remove();
        delete participantMarkersRef.current[oddc];
      }
    });

    // Add/update markers for each participant
    participants.forEach(participant => {
      // Clamp progress to route length
      const clampedDistance = Math.min(participant.currentDistanceMeters || 0, totalDistance);
      const position = getPositionAtDistance(
        coordinates,
        cumulativeDistancesRef.current,
        clampedDistance
      );

      if (participantMarkersRef.current[participant.oddc]) {
        participantMarkersRef.current[participant.oddc].setLngLat(position);
      } else {
        const el = document.createElement('div');
        el.className = 'participant-marker';
        if (participant.oddc === user?.uid) {
          el.classList.add('is-current-user');
        }
        if (participant.photoURL) {
          el.innerHTML = `<img src="${participant.photoURL}" alt="${participant.displayName}" />`;
        } else {
          el.innerHTML = `<span>${(participant.displayName || '?')[0]}</span>`;
        }

        participantMarkersRef.current[participant.oddc] = new mapboxgl.Marker(el)
          .setLngLat(position)
          .addTo(map.current);
      }
    });
  }, [participants, config, user, mapReady]);

  // Load journey activities from Strava via existing getStravaActivityById function
  const loadJourneyActivities = useCallback(async () => {
    if (!participantData?.activities?.length || isLoadingJourney || journeyLoaded) return;
    setIsLoadingJourney(true);

    const getActivityById = httpsCallable(functions, 'getStravaActivityById');
    const activities = participantData.activities;
    const loaded = [];

    // Fetch in small batches of 3
    for (let i = 0; i < activities.length; i += 3) {
      const batch = activities.slice(i, i + 3);
      const results = await Promise.allSettled(
        batch.map(a => getActivityById({ activityId: String(a.activityId) }))
      );
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value?.data) {
          loaded.push({
            ...result.value.data,
            _challengeMeta: batch[idx], // Keep the challenge metadata (cumulativeDistance etc.)
          });
        }
      });
      setJourneyActivities([...loaded]);
    }

    setJourneyLoaded(true);
    setIsLoadingJourney(false);
  }, [participantData, isLoadingJourney, journeyLoaded]);

  // Load journey activities immediately when participant data is available
  useEffect(() => {
    if (!participantData?.activities?.length || journeyLoaded || !user || isLoadingJourney) return;
    loadJourneyActivities();
  }, [participantData, journeyLoaded, user, isLoadingJourney, loadJourneyActivities]);

  // Auto-append challenge progress popup trigger
  useEffect(() => {
    if (autoAppendPopupCheckedRef.current) return;
    if (!hasJoined || !user?.uid || !participantData?.activities) return;
    if (participantData.activities.length < 1) return;

    autoAppendPopupCheckedRef.current = true;

    const checkPopupEligibility = async () => {
      try {
        // Check user settings
        const userDocRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userDocRef);
        if (!userSnap.exists()) return;
        const userData = userSnap.data();

        // Don't show if already dismissed
        if (userData?.settings?.autoAppendPopupDismissed === true) return;

        // Don't show if reward already claimed
        const rewardDocRef = doc(db, 'users', user.uid, 'creditRewards', 'autoAppendChallenge');
        const rewardSnap = await getDoc(rewardDocRef);
        if (rewardSnap.exists() && rewardSnap.data()?.claimed) return;

        // Check if already enabled (will show different copy). The new
        // canonical preference is settings.challengeProgressUpload.enabled
        // which defaults to true; the legacy autoAppendMythicLink is also
        // honored for users who opted in before the merge.
        const challengeProgressUploadEnabled = userData?.settings?.challengeProgressUpload?.enabled !== false;
        const legacyAutoAppend = userData?.settings?.autoAppendMythicLink === true;
        const alreadyEnabled = challengeProgressUploadEnabled || legacyAutoAppend;
        setAutoAppendAlreadyEnabled(alreadyEnabled);
        setShowAutoAppendPopup(true);
        posthog?.capture('challenge_auto_append_popup_shown', {
          page: 'challenge_detail',
          challenge_id: configId,
          already_enabled: alreadyEnabled,
          activity_count: participantData?.activities?.length || 0,
        });
      } catch (err) {
        console.error('Error checking auto-append popup eligibility:', err);
      }
    };

    checkPopupEligibility();
  }, [hasJoined, user, participantData]);

  // PostHog: track challenge page load (once per mount after config settles)
  useEffect(() => {
    if (challengePageLoadedRef.current || !config) return;
    challengePageLoadedRef.current = true;
    const activityCount = participantData?.activities?.length || 0;
    const progressMeters = participantData?.currentDistanceMeters || 0;
    const totalDistM = totalRouteDistanceM;
    const checkpointsReached = participantData?.checkpointsReached?.length || 0;
    posthog?.capture('challenge_page_loaded', {
      page: 'challenge_detail',
      challenge_id: configId,
      has_joined: hasJoined,
      is_completed: hasJoined && isChallengeParticipantComplete(participantData, progressMeters, config?.route),
      user_progress_meters: progressMeters,
      user_progress_percentage: totalDistM > 0 ? Math.round((progressMeters / totalDistM) * 100) : 0,
      checkpoints_reached: checkpointsReached,
      activity_count: activityCount,
      total_participants: participants.length,
    });
  }, [config, configId, hasJoined, participantData, participants.length, posthog, totalRouteDistanceM]);

  const pickBestGeneratedMap = useCallback((generations) => {
    if (!Array.isArray(generations) || !generations.length) return null;

    const completed = generations.filter((generation) => (
      generation?.status === 'openai_processing_complete' &&
      (!!generation?.generatedImageUrl || !!generation?.generatedImagePath)
    ));
    if (!completed.length) return null;

    const byUser = user?.uid
      ? completed.filter((generation) => String(generation?.userId || '') === String(user.uid))
      : completed;

    const byChallenge = byUser.filter((generation) => (
      generation?.isChallengeGeneration === true &&
      String(generation?.challengeId || '') === String(configId)
    ));

    return byChallenge[0] || byUser[0] || completed[0];
  }, [configId, user?.uid]);

  // Resolve generated maps for each activity once, then reuse in timeline and map popups.
  useEffect(() => {
    if (!hasJoined || !participantData?.activities?.length) {
      setActivityGenerationMap({});
      return;
    }

    let cancelled = false;
    const getGenerations = httpsCallable(functions, 'getPreviousGenerationsByStravaId');

    const loadGeneratedMaps = async () => {
      const results = await Promise.allSettled(
        participantData.activities.map((activity) =>
          getGenerations({ activityId: String(activity.activityId) })
        )
      );

      if (cancelled) return;

      const nextMap = {};
      for (let index = 0; index < results.length; index += 1) {
        const result = results[index];
        if (result.status !== 'fulfilled') continue;
        const generations = result.value?.data?.generations || result.value?.data || [];
        const best = pickBestGeneratedMap(generations);
        if (!best?.id) continue;

        let resolvedImageUrl = best.generatedImageUrl || null;
        if (!resolvedImageUrl && best.generatedImagePath) {
          try {
            resolvedImageUrl = await getDownloadURL(ref(storage, best.generatedImagePath));
          } catch (urlError) {
            console.warn('[Challenge] Failed to resolve generated image URL:', best.generatedImagePath, urlError);
          }
        }

        const rawActivity = participantData.activities[index];
        nextMap[String(rawActivity.activityId)] = {
          generationId: best.id,
          generatedImageUrl: resolvedImageUrl,
        };
      }

      setActivityGenerationMap(nextMap);
    };

    loadGeneratedMaps();

    return () => {
      cancelled = true;
    };
  }, [hasJoined, participantData, pickBestGeneratedMap]);

  // Navigate to create page with challenge context
  const handleCreateMapFromJourney = useCallback((activity) => {
    if (!config?.route?.checkpoints || !checkpointDistances.length) return;
    const cumulativeDist = activity._challengeMeta?.cumulativeDistance || 0;
    const style = getStyleForDistance(challengeActs, cumulativeDist, config.route.checkpoints, checkpointDistances);
    posthog?.capture('challenge_create_map_clicked', { page: 'challenge_detail', challenge_id: configId, activity_id: String(activity.id || ''), style });
    navigate(`/create/${activity.id}?challenge=${configId}&style=${encodeURIComponent(style)}&free=true`);
  }, [config, checkpointDistances, configId, navigate, posthog]);

  const handleOpenGeneratedMap = useCallback((requestId) => {
    if (!requestId) return;
    posthog?.capture('challenge_view_generated_map', { page: 'challenge_detail', challenge_id: configId, request_id: requestId });
    navigate(`/manage-post/${requestId}`);
  }, [navigate, posthog, configId]);

  // Download checkpoint image via blob (more reliable than <a download>)
  const handleCheckpointDownload = async () => {
    if (isDownloadingCheckpoint || !selectedCheckpoint?.imageUrl) return;
    setIsDownloadingCheckpoint(true);
    try {
      const response = await fetch(selectedCheckpoint.imageUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checkpoint-${selectedCheckpoint.globalIdx + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      posthog?.capture('challenge_checkpoint_downloaded', {
        page: 'challenge_detail',
        challenge_id: configId,
        checkpoint_index: selectedCheckpoint.globalIdx,
        checkpoint_name: selectedCheckpoint.name || `Checkpoint ${selectedCheckpoint.globalIdx + 1}`,
      });
    } catch (error) {
      console.error('Error downloading checkpoint image:', error);
    } finally {
      setIsDownloadingCheckpoint(false);
    }
  };

  // Share checkpoint on social media (links to challenge page)
  const handleCheckpointShare = (platform) => {
    posthog?.capture('challenge_checkpoint_shared', {
      page: 'challenge_detail',
      challenge_id: configId,
      checkpoint_index: selectedCheckpoint?.globalIdx,
      share_platform: platform,
    });
    const shareUrl = `${window.location.origin}/challenge/${configId}`;
    const cpName = selectedCheckpoint?.name || `Checkpoint ${selectedCheckpoint?.globalIdx + 1}`;
    const shareText = `I reached ${cpName} on the ${challengeCatalogEntry?.title || config?.name || 'Mythic'} Challenge on Mythic Maps!`;
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'instagram':
      case 'tiktok':
        handleCheckpointDownload();
        break;
      case 'reddit':
        window.open(`https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`, '_blank');
        break;
      default:
        break;
    }
  };

  // Handle Strava login for challenge join
  const handleStravaConnect = useCallback(() => {
    posthog?.capture('challenge_onboarding_strava_connect', { page: 'challenge_detail', challenge_id: configId, onboarding_step: 1 });
    const STRAVA_CLIENT_ID = "159733";
    const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
    const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";

    // Store current page URL to redirect back after login
    localStorage.setItem('stravaOAuthReturnUrl', window.location.pathname);

    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('stravaOAuthState', state);

    const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
    window.location.href = consentUrl;
  }, [posthog, configId]);

  // Handle join button click — opens onboarding modal
  const handleJoinClick = useCallback(() => {
    posthog?.capture('challenge_join_clicked', { page: 'challenge_detail', challenge_id: configId, is_authenticated: !!user });
    if (!user) {
      setOnboardingStep(1); // Step 1: Connect Strava
    } else {
      setOnboardingStep(2); // Skip to Step 2: Profile review
    }
  }, [user, posthog, configId]);

  // Lookup the user's active challenge so we can gate the join button.
  // Skip when the user is already a participant here (hasJoined) — they own
  // the active slot on this challenge, no need to look further.
  useEffect(() => {
    if (!user?.uid || !configId || hasJoined) {
      setActiveChallengeInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getActiveChallenge');
        const result = await fn({});
        if (cancelled) return;
        const data = result?.data;
        if (data?.active && data.challengeId && data.challengeId !== configId) {
          setActiveChallengeInfo({ challengeId: data.challengeId, title: data.title });
        } else {
          setActiveChallengeInfo(null);
        }
      } catch (err) {
        // Non-fatal — fall back to letting the backend gate the actual join call.
        console.warn('[getActiveChallenge] lookup failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid, configId, hasJoined]);

  // Restart flow — open a styled modal, then call the backend on confirm.
  const requestRestartChallenge = useCallback(() => {
    if (!user?.uid || isRestarting) return;
    setRestartError(null);
    setShowRestartModal(true);
  }, [user?.uid, isRestarting]);

  const closeRestartModal = useCallback(() => {
    if (isRestarting) return;
    setShowRestartModal(false);
    setRestartError(null);
  }, [isRestarting]);

  const confirmRestartChallenge = useCallback(async () => {
    if (!user?.uid || isRestarting) return;
    setIsRestarting(true);
    setRestartError(null);
    try {
      const fn = httpsCallable(functions, 'restartMapChallenge');
      await fn({ configId });
      posthog?.capture('challenge_restart_succeeded', { page: 'challenge_detail', challenge_id: configId });
      // Re-pull participant doc so the UI flips from completed back to in-progress.
      const snap = await getDoc(doc(db, 'gpxMapConfigs', configId, 'participants', user.uid));
      if (snap.exists()) {
        setParticipantData(snap.data());
      }
      setShowRestartModal(false);
    } catch (err) {
      const details = err?.details;
      if (details?.code === 'already-active') {
        setRestartError(`Finish ${details.activeChallengeTitle || 'your active challenge'} before restarting.`);
      } else {
        console.error('[challenge restart] failed:', err);
        setRestartError(err?.message || 'Could not restart the challenge. Please try again.');
      }
      posthog?.capture('challenge_restart_failed', { page: 'challenge_detail', challenge_id: configId, error_code: details?.code || 'unknown' });
    } finally {
      setIsRestarting(false);
    }
  }, [user?.uid, isRestarting, configId, posthog]);

  // Debug: allow forcing onboarding step via URL param (e.g. ?testOnboarding=2)
  useEffect(() => {
    if (viewerOnly) return; // the embed reads the parent /challenge URL; never auto-open onboarding
    const params = new URLSearchParams(window.location.search);
    const testStep = parseInt(params.get('testOnboarding'));
    if (testStep >= 1 && testStep <= 3) {
      setOnboardingStep(testStep);
    }
  }, [viewerOnly]);

  // Initialize profile fields when entering step 2
  useEffect(() => {
    if (onboardingStep === 2 && user) {
      setProfileName(user.displayName || '');
      setProfilePhotoPreview(user.photoURL || null);
      setProfilePhotoFile(null);
    }
  }, [onboardingStep, user]);

  // Fetch latest Strava activity when entering step 3
  useEffect(() => {
    if (onboardingStep === 3 && user) {
      setLoadingLatestActivity(true);
      setLatestActivity(null);
      const getActivities = httpsCallable(functions, 'getStravaActivities');
      getActivities({ challengeMode: true }).then(result => {
        const activities = result.data?.activities || result.data || [];
        if (Array.isArray(activities) && activities.length > 0) {
          setLatestActivity(activities[0]);
        }
      }).catch(err => {
        console.error('Failed to fetch latest activity:', err);
      }).finally(() => setLoadingLatestActivity(false));
    }
  }, [onboardingStep, user]);

  // Step 2: Handle photo file selection
  const handlePhotoSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfilePhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setProfilePhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  // Step 2: Save profile, join the challenge, then advance to the import step.
  // The join lives here because the modal no longer has a separate email step;
  // without it nothing would create the participant document and the import step
  // would fail with "You must join the challenge first".
  const handleSaveProfile = useCallback(async () => {
    if (!user) return;
    setIsSavingProfile(true);
    setJoinError('');
    try {
      let photoURL = null;

      // Upload new photo if selected
      if (profilePhotoFile) {
        const photoRef = ref(storage, `users/${user.uid}/challenge-profile.jpg`);
        await uploadBytes(photoRef, profilePhotoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      // Call backend to update user doc + all participant docs
      const updateFunc = httpsCallable(functions, 'updateChallengeProfile');
      const payload = {};
      if (profileName.trim()) {
        payload.displayName = profileName.trim();
      }
      if (photoURL) {
        payload.photoURL = photoURL;
      }
      await updateFunc(payload);

      posthog?.capture('challenge_onboarding_profile_saved', {
        page: 'challenge_detail',
        challenge_id: configId,
        has_custom_photo: !!profilePhotoFile,
        has_custom_name: !!profileName.trim() && profileName.trim() !== user.displayName,
      });

      try {
        const joinFunc = httpsCallable(functions, 'joinMapChallenge');
        await joinFunc({ configId });
      } catch (joinErr) {
        // Already a participant is a no-op, not a failure. Anything else
        // (notably the one-active-challenge rule) has to be shown to the user.
        if (joinErr?.code !== 'functions/already-exists') {
          throw joinErr;
        }
      }

      posthog?.capture('challenge_joined', { page: 'challenge_detail', challenge_id: configId });
      setHasJoined(true);
      setOnboardingStep(3);
    } catch (err) {
      console.error('Failed to join challenge:', err);
      posthog?.capture('challenge_join_failed', {
        page: 'challenge_detail',
        challenge_id: configId,
        error_message: err.message || 'Unknown error',
      });
      setJoinError(err.message || 'Failed to join challenge. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  }, [user, profileName, profilePhotoFile, posthog, configId]);

  // Step 3: Import latest activity
  const handleImportActivity = useCallback(async () => {
    if (!latestActivity) return;
    setImportingActivity(true);
    try {
      const importFunc = httpsCallable(functions, 'importActivityToChallenge');
      const result = await importFunc({ configId });
      if (result.data?.success === false) {
        if (result.data.reason === 'no_eligible_activities') {
          setLatestActivity(null); // Clear to show "no activities" state
          return;
        }
      }
      // Refresh participant data
      const participantRef = doc(db, 'gpxMapConfigs', configId, 'participants', user.uid);
      const participantSnap = await getDoc(participantRef);
      if (participantSnap.exists()) {
        const updatedData = participantSnap.data();
        setParticipantData(updatedData);
        // Re-check auto-append popup if this was the 1st+ activity
        if (updatedData?.activities?.length >= 1) {
          autoAppendPopupCheckedRef.current = false;
        }
      }
      posthog?.capture('challenge_onboarding_activity_imported', {
        page: 'challenge_detail',
        challenge_id: configId,
        activity_id: String(latestActivity.id || latestActivity.activityId || ''),
        distance_meters: latestActivity.distance || 0,
      });
      setOnboardingStep(0);
    } catch (err) {
      console.error('Failed to import activity:', err);
    } finally {
      setImportingActivity(false);
    }
  }, [latestActivity, configId, user, posthog]);

  // Step 4: Skip import
  const handleSkipImport = useCallback(() => {
    posthog?.capture('challenge_onboarding_import_skipped', { page: 'challenge_detail', challenge_id: configId });
    setOnboardingStep(0);
  }, [posthog, configId]);

  // Remove an activity from the challenge
  const handleRemoveActivity = useCallback(async (activityId) => {
    if (!activityId || !configId || !user?.uid) return;
    setDeletingActivityId(activityId);
    try {
      const removeFunc = httpsCallable(functions, 'removeActivityFromChallenge');
      const result = await removeFunc({ configId, activityId });
      if (result.data?.success) {
        posthog?.capture('challenge_activity_removed', { page: 'challenge_detail', challenge_id: configId, activity_id: activityId });
        // Refresh participant data
        const participantRef = doc(db, 'gpxMapConfigs', configId, 'participants', user.uid);
        const participantSnap = await getDoc(participantRef);
        if (participantSnap.exists()) {
          setParticipantData(participantSnap.data());
        }
      } else {
        console.error('[CHALLENGE] Remove activity failed:', result.data?.reason);
      }
    } catch (err) {
      console.error('[CHALLENGE] Failed to remove activity:', err);
    } finally {
      setDeletingActivityId(null);
    }
  }, [configId, user, posthog]);

  // Bearing smoothing function - applies exponential smoothing and history averaging
  const smoothBearing = useCallback((targetBearing) => {
    // Apply exponential smoothing first
    const smoothingFactor = 0.08;
    let currentBearing = currentBearingRef.current;
    let delta = targetBearing - currentBearing;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    const newBearing = currentBearing + delta * smoothingFactor;

    // Normalize to 0-360
    const normalizedBearing = ((newBearing % 360) + 360) % 360;

    // Add to history buffer for additional smoothing
    bearingHistoryRef.current.push(normalizedBearing);
    if (bearingHistoryRef.current.length > BEARING_HISTORY_SIZE) {
      bearingHistoryRef.current.shift();
    }

    // Calculate weighted average of history (more recent = higher weight)
    let weightedSum = 0;
    let weightTotal = 0;
    const history = bearingHistoryRef.current;

    for (let i = 0; i < history.length; i++) {
      const weight = i + 1; // Linear weights: 1, 2, 3, ...
      // Handle wraparound in averaging
      let bearing = history[i];
      if (history.length > 1) {
        const refBearing = history[history.length - 1];
        let diff = bearing - refBearing;
        if (diff > 180) bearing -= 360;
        if (diff < -180) bearing += 360;
      }
      weightedSum += bearing * weight;
      weightTotal += weight;
    }

    const smoothedBearing = ((weightedSum / weightTotal % 360) + 360) % 360;
    currentBearingRef.current = smoothedBearing;

    return currentBearingRef.current;
  }, []);

  // Update puck and camera position for a given distance (used by both animation and scrubbing)
  const updatePositionAtDistance = useCallback((distance, smooth = true, options = {}) => {
    if (!config?.route?.coordinates || !map.current || !cumulativeDistancesRef.current) return;

    const coordinates = config.route.coordinates;
    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];
    const maxView = maxViewableDistanceRef.current;
    const ignoreViewLimit = options?.ignoreViewLimit === true;
    const upperBound = (!ignoreViewLimit && config?.isChallenge && maxView > 0 && maxView < Infinity) ?
      Math.min(totalDistance, maxView) :
      totalDistance;
    const clampedDistance = Math.max(0, Math.min(distance, upperBound));

    const position = getPositionAtDistance(
      coordinates,
      cumulativeDistancesRef.current,
      clampedDistance
    );

    const targetBearing = getBearingAtDistance(
      coordinates,
      cumulativeDistancesRef.current,
      clampedDistance
    );

    // Apply smoothing for scrubbing, instant for animation start
    const bearing = smooth ? smoothBearing(targetBearing) : targetBearing;
    if (!smooth) {
      currentBearingRef.current = targetBearing;
    }

    // Update puck position
    if (puckMarkerRef.current) {
      puckMarkerRef.current.setLngLat(position);
    }

    // Use FreeCamera API to position camera behind the puck
    const behindBearing = (bearing + 180) % 360;
    const cameraPosition = offsetPosition(position, behindBearing, cameraDistanceRef.current);

    const camera = map.current.getFreeCameraOptions();
    // Preserve current altitude if user has zoomed, otherwise use default
    const currentAltitude = camera.position?.toAltitude() || cameraAltitudeRef.current;
    camera.position = mapboxgl.MercatorCoordinate.fromLngLat(cameraPosition, currentAltitude);
    camera.lookAtPoint(position);
    map.current.setFreeCameraOptions(camera);

    currentDistanceRef.current = clampedDistance;
    setCurrentDistance(clampedDistance);
    setAnimationProgress(clampedDistance / totalDistance);
    updateCheckpointPopups(clampedDistance);
    updateActivityPopups(clampedDistance);
    updateRouteGradient(clampedDistance);
    applyRouteMapStyleAtDistance(clampedDistance);
  }, [config, smoothBearing, updateRouteGradient, applyRouteMapStyleAtDistance]);

  useEffect(() => {
    if (!mapReady || !config?.isChallenge || !hasJoined || userProgress <= 0) return;
    if (didSyncChallengeProgressViewRef.current) return;

    didSyncChallengeProgressViewRef.current = true;
    updatePositionAtDistance(userProgress, false, { ignoreViewLimit: true });
  }, [mapReady, config?.isChallenge, hasJoined, userProgress, updatePositionAtDistance]);

  const scrollToTopForFlyover = useCallback(() => {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    const findScrollableAncestor = (startEl) => {
      let node = startEl?.parentElement;
      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY || style.overflow;
        const isScrollable = /(auto|scroll)/i.test(overflowY);
        if (isScrollable && node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const activeScroller = findScrollableAncestor(mapContainer.current);
    const initialScrollerTop = activeScroller?.scrollTop ?? 0;
    const initialDocTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const shouldTrackScroller = !!activeScroller && initialScrollerTop > 2;
    const shouldTrackWindow = initialDocTop > 2 || !shouldTrackScroller;

    if (activeScroller && typeof activeScroller.scrollTo === 'function') {
      activeScroller.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });

    return new Promise((resolve) => {
      const startedAt = performance.now();
      const maxWaitMs = 3000;

      const check = () => {
        const scrollerTop = activeScroller ? activeScroller.scrollTop : 0;
        const docTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const scrollerDone = !shouldTrackScroller || scrollerTop <= 2;
        const windowDone = !shouldTrackWindow || docTop <= 2;
        const reachedTarget = scrollerDone && windowDone;
        const timedOut = performance.now() - startedAt > maxWaitMs;

        if (reachedTarget || timedOut) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };

      requestAnimationFrame(check);
    });
  }, []);

  const handleActivityFlyover = useCallback(async (event) => {
    if (!event || !map.current || !config?.route?.coordinates || !cumulativeDistancesRef.current) return;
    const runId = flyoverRunIdRef.current + 1;
    flyoverRunIdRef.current = runId;

    await scrollToTopForFlyover();
    if (flyoverRunIdRef.current !== runId) return;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (flyoverAnimationRef.current) {
      cancelAnimationFrame(flyoverAnimationRef.current);
      flyoverAnimationRef.current = null;
    }

    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1] || 0;
    const segmentEnd = Math.max(0, Math.min(Number(event.distance) || 0, totalDistance));
    const segmentDistance = Math.max(0, Number(event.distanceMeters) || 0);
    const segmentStart = Math.max(0, segmentEnd - segmentDistance);
    const clampedStart = Math.max(0, Math.min(segmentStart, totalDistance));
    const clampedEnd = Math.max(0, Math.min(segmentEnd, totalDistance));
    const flyoverDistance = Math.max(0, clampedEnd - clampedStart);

    console.log('[FLYOVER] Starting activity flyover', {
      activityId: event.activityId,
      start: Math.round(clampedStart),
      end: Math.round(clampedEnd),
      distance: Math.round(flyoverDistance)
    });
    posthog?.capture('challenge_activity_flyover_started', { page: 'challenge_detail', challenge_id: configId, activity_id: String(event.activityId || '') });

    // Flyover uses a separate RAF loop from the default route animation.
    setIsAnimating(false);
    setIsPaused(false);
    setIsScrubMode(false);
    if (map.current) {
      map.current.dragPan.enable();
    }

    updatePositionAtDistance(clampedStart, false);

    if (flyoverDistance <= 1) {
      setIsScrubMode(true);
      if (map.current) {
        map.current.dragPan.disable();
      }
      return;
    }

    // Segment-only flyover timing: slightly faster than the previous version.
    const durationMs = Math.max(5000, Math.min(26000, flyoverDistance * 0.65));
    const startTime = performance.now();

    const step = (timestamp) => {
      const elapsed = timestamp - startTime;
      const t = Math.max(0, Math.min(1, elapsed / durationMs));
      const eased = t < 0.5
        ? 2 * t * t
        : 1 - (Math.pow(-2 * t + 2, 2) / 2);
      const targetDistance = clampedStart + (flyoverDistance * eased);
      updatePositionAtDistance(targetDistance);

      if (t < 1) {
        flyoverAnimationRef.current = requestAnimationFrame(step);
      } else {
        flyoverAnimationRef.current = null;
        updatePositionAtDistance(clampedEnd, false);
        setIsScrubMode(true);
        if (map.current) {
          map.current.dragPan.disable();
        }
      }
    };

    flyoverAnimationRef.current = requestAnimationFrame(step);
  }, [config, updatePositionAtDistance, scrollToTopForFlyover, posthog, configId]);

  // Auto-open/close checkpoint popups based on camera distance along route
  const updateCheckpointPopups = (cameraDistance) => {
    const markers = checkpointMarkersRef.current;
    const distances = checkpointDistancesRef.current;
    if (!markers.length || !distances.length || !map.current) return;

    const RANGE = 5000;
    const FADE_ZONE = 1000;

    const maxView = maxViewableDistanceRef.current;

    // First pass: find the closest checkpoint within range
    let closestIdx = -1;
    let closestDiff = Infinity;

    markers.forEach((marker, i) => {
      const cpDist = distances[i];
      if (cpDist === undefined) return;
      // Skip checkpoints beyond the fog boundary
      if (config?.isChallenge && maxView < Infinity && cpDist > maxView) return;
      const absDiff = Math.abs(cpDist - cameraDistance);
      if (absDiff <= RANGE && absDiff < closestDiff) {
        closestDiff = absDiff;
        closestIdx = i;
      }
    });

    // Second pass: only show popup for the closest checkpoint
    markers.forEach((marker, i) => {
      const cpDist = distances[i];
      if (cpDist === undefined) return;

      const diff = cpDist - cameraDistance;
      const absDiff = Math.abs(diff);
      const popup = marker.getPopup();
      if (!popup) return;

      if (absDiff <= RANGE && i === closestIdx) {
        if (!openCheckpointPopupsRef.current.has(i)) {
          popup.addTo(map.current);
          openCheckpointPopupsRef.current.add(i);
        }
        let opacity = 1;
        const innerRange = RANGE - FADE_ZONE;
        if (diff > innerRange) {
          opacity = (RANGE - diff) / FADE_ZONE;
        } else if (diff < -innerRange) {
          opacity = (RANGE + diff) / FADE_ZONE;
        }
        const popupEl = popup.getElement();
        if (popupEl) {
          popupEl.style.opacity = String(Math.max(0, Math.min(1, opacity)));
        }
      } else {
        if (openCheckpointPopupsRef.current.has(i)) {
          popup.remove();
          openCheckpointPopupsRef.current.delete(i);
        }
      }
    });
  };

  // Auto-open/close activity popups based on camera distance along route
  const updateActivityPopups = (cameraDistance) => {
    const markers = activityMarkersRef.current;
    const ranges = activityDistanceRangesRef.current;
    if (!markers.length || !ranges.length || !map.current) return;

    const RANGE = 5000;
    const FADE_ZONE = 1000;

    // First pass: find the closest activity midpoint within range
    let closestIdx = -1;
    let closestDiff = Infinity;

    markers.forEach((marker, i) => {
      const { midpoint } = ranges[i];
      if (midpoint === undefined) return;
      const absDiff = Math.abs(midpoint - cameraDistance);
      if (absDiff <= RANGE && absDiff < closestDiff) {
        closestDiff = absDiff;
        closestIdx = i;
      }
    });

    // Suppression: never show activity popup while a checkpoint popup is open
    if (closestIdx !== -1 && openCheckpointPopupsRef.current.size > 0) {
      closestIdx = -1;
    }

    // Second pass: only show popup for the closest activity
    markers.forEach((marker, i) => {
      const { midpoint } = ranges[i];
      if (midpoint === undefined) return;

      const diff = midpoint - cameraDistance;
      const absDiff = Math.abs(diff);
      const popup = marker.getPopup();
      if (!popup) return;

      if (absDiff <= RANGE && i === closestIdx) {
        if (!openActivityPopupsRef.current.has(i)) {
          popup.addTo(map.current);
          openActivityPopupsRef.current.add(i);
        }
        let opacity = 1;
        const innerRange = RANGE - FADE_ZONE;
        if (diff > innerRange) {
          opacity = (RANGE - diff) / FADE_ZONE;
        } else if (diff < -innerRange) {
          opacity = (RANGE + diff) / FADE_ZONE;
        }
        const popupEl = popup.getElement();
        if (popupEl) {
          popupEl.style.opacity = String(Math.max(0, Math.min(1, opacity)));
        }
      } else {
        if (openActivityPopupsRef.current.has(i)) {
          popup.remove();
          openActivityPopupsRef.current.delete(i);
        }
      }
    });
  };

  // Add route to map
  const addRouteToMap = useCallback((coordinates, checkpoints = []) => {
    if (!map.current) return;

    // Add route source
    if (!map.current.getSource('route')) {
      map.current.addSource('route', {
        type: 'geojson',
        lineMetrics: true,
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          }
        }
      });

      // Add route outline (wider, darker)
      map.current.addLayer({
        id: 'route-outline',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#000000',
          'line-width': 6,
          'line-opacity': 0.5
        }
      });

      // Add route line
      map.current.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-gradient': [
            'interpolate', ['linear'], ['line-progress'],
            0, routeColorsRef.current.current,
            1, routeColorsRef.current.current
          ],
          'line-width': 4,
          'line-trim-offset': [1.0, 1.0],
          'line-trim-color': '#d4c4b0'
        }
      });

      // Add checkpoint markers (if present)
      checkpointMarkersRef.current.forEach((m) => m.remove());
      checkpointMarkersRef.current = [];

      if (Array.isArray(checkpoints) && checkpoints.length > 0) {
        checkpoints.forEach((cp, i) => {
          if (typeof cp?.lon !== 'number' || typeof cp?.lat !== 'number') return;

          const label = cp.isStart && cp.isEnd
            ? 'S/F'
            : cp.isStart
              ? 'S'
              : cp.isEnd
                ? 'F'
                : `${i + 1}`;

          const el = document.createElement('div');
          el.className = 'checkpoint-marker';
          el.innerHTML = `<div class="checkpoint-marker-inner">${label}</div>`;

          const title = escapeHtml(cp.name || `Checkpoint ${i + 1}`);
          const cpDist = checkpointDistancesRef.current[i];
          const distLabel = cpDist !== undefined ? `${(cpDist / 1000).toFixed(1)} km` : '';
          const meta = `<div class="checkpoint-popup-meta">Checkpoint ${i + 1}${distLabel ? ` \u2022 ${distLabel}` : ''}</div>`;
          const desc = cp.desc ? `<div class="checkpoint-popup-desc">${escapeHtml(cp.desc)}</div>` : '';
          const image = cp.imageUrl
            ? `<div class="checkpoint-popup-image"><img src="${escapeHtml(cp.imageUrl)}" alt="${title}"></div>`
            : '';

          const popup = new mapboxgl.Popup({ offset: 18, closeOnClick: false, closeButton: false, anchor: 'left' }).setHTML(
            `<div class="checkpoint-popup">
              ${image}
              <div class="checkpoint-popup-body">
                <div class="checkpoint-popup-title">${title}</div>
                ${meta}
                ${desc}
              </div>
            </div>`
          );

          const marker = new mapboxgl.Marker(el)
            .setLngLat([cp.lon, cp.lat])
            .setPopup(popup)
            .addTo(map.current);

          checkpointMarkersRef.current.push(marker);
        });
      }
    }
  }, []);

  // Hide checkpoint markers beyond the fog boundary
  const updateCheckpointMarkerVisibility = useCallback(() => {
    if (!config?.isChallenge) return;
    const maxView = maxViewableDistanceRef.current;
    const distances = checkpointDistancesRef.current;

    checkpointMarkersRef.current.forEach((marker, i) => {
      const cpDist = distances[i];
      if (cpDist !== undefined && maxView < Infinity && cpDist > maxView) {
        marker.getElement().style.display = 'none';
        const popup = marker.getPopup();
        if (popup && openCheckpointPopupsRef.current.has(i)) {
          popup.remove();
          openCheckpointPopupsRef.current.delete(i);
        }
      } else {
        marker.getElement().style.display = '';
      }
    });
  }, [config?.isChallenge]);

  // Update fog-of-war marker visibility when maxViewableDistance changes
  useEffect(() => {
    if (!mapReady || !config?.isChallenge) return;
    updateCheckpointMarkerVisibility();
  }, [maxViewableDistance, mapReady, config?.isChallenge, updateCheckpointMarkerVisibility]);

  // Add processed tiles to map
  const addProcessedTilesToMap = useCallback(() => {
    if (!map.current) return;

    const tilesConfig = tilesConfigRef.current;
    const basePath = config?.tiles?.basePath;
    const tileKeys = tilesConfig?.tileKeys || [];
    const tileZoomRange = tilesConfig?.tileZoomRange;
    const hasCustomTiles = Boolean(basePath) && tileKeys.length > 0;
    // Don't add a background fill - let the base map style show through
    const fillColor = null;

    if (fillColor) {
      if (!map.current.getSource('background-source')) {
        map.current.addSource('background-source', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [[
                [-180, -85],
                [180, -85],
                [180, 85],
                [-180, 85],
                [-180, -85]
              ]]
            }
          }
        });

        const beforeLayerId = map.current.getLayer('route-outline') ? 'route-outline' : undefined;

        map.current.addLayer({
          id: 'background-layer',
          type: 'fill',
          source: 'background-source',
          paint: {
            'fill-color': fillColor,
            'fill-opacity': 1
          }
        }, beforeLayerId);
      }
    }

    // Add raster tile source so Mapbox requests tiles on demand
    if (basePath) {
      const sourceId = 'custom-tiles';
      const layerId = 'custom-tiles-layer';
      const beforeLayerId = map.current.getLayer('route-outline') ? 'route-outline' : undefined;
      const tileUrlTemplate = getTileUrlTemplate(basePath);
      tileUrlTemplateRef.current = tileUrlTemplate;
      tileBasePathPrefixRef.current = tileUrlTemplate.split('{z}')[0];

      if (!map.current.getSource(sourceId)) {
        console.log('[TILES_SOURCE] Using template:', tileUrlTemplate);
        map.current.addSource(sourceId, {
          type: 'raster',
          tiles: [tileUrlTemplate],
          tileSize: 512,
          minzoom: tileZoomRange?.minZoom ?? CUSTOM_TILE_ZOOM,
          maxzoom: tileZoomRange?.maxZoom ?? CUSTOM_TILE_ZOOM
        });
      }

      if (!map.current.getLayer(layerId)) {
        map.current.addLayer({
          id: layerId,
          type: 'raster',
          source: sourceId,
          paint: {
            'raster-opacity': 1,
            'raster-fade-duration': 300
          }
        }, beforeLayerId);
      }
    }

    // Add gradient fade strips on outer edges of tile grid (only once, only if fill color is set)
    if (fillColor && hasCustomTiles && !map.current.getSource('edge-fade-source') && tilesConfig?.tileKeys?.length) {
      const bgColor = fillColor;
      const numStrips = config?.display?.borderFadeStrips || 30;
      const fadeWidthPercent = config?.display?.borderFadeWidth || 5;
      const features = [];

      // Build a set of ALL tile keys for edge detection (not just loaded ones)
      const tileSet = new Set(tilesConfig.tileKeys);

      // Process loaded tiles to find outer edges
      tileKeys.forEach((key) => {
        const [z, x, y] = key.split('/').map(Number);

        // Calculate tile bounds
        const n = Math.pow(2, z);
        const tileWest = (x / n) * 360 - 180;
        const tileEast = ((x + 1) / n) * 360 - 180;
        const northRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * y / n)));
        const southRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * (y + 1) / n)));
        const tileNorth = northRad * 180 / Math.PI;
        const tileSouth = southRad * 180 / Math.PI;

        const tileWidth = tileEast - tileWest;
        const tileHeight = tileNorth - tileSouth;
        const fadeWidth = Math.min(tileWidth, tileHeight) * (fadeWidthPercent / 100);

        const hasWest = tileSet.has(`${z}/${x - 1}/${y}`);
        const hasEast = tileSet.has(`${z}/${x + 1}/${y}`);
        const hasNorth = tileSet.has(`${z}/${x}/${y - 1}`);
        const hasSouth = tileSet.has(`${z}/${x}/${y + 1}`);

        if (!hasWest) {
          for (let i = 0; i < numStrips; i++) {
            const opacity = (numStrips - i) / numStrips;
            const stripWest = tileWest + fadeWidth * i / numStrips;
            const stripEast = tileWest + fadeWidth * (i + 1) / numStrips;
            features.push({
              type: 'Feature',
              properties: { opacity },
              geometry: {
                type: 'Polygon',
                coordinates: [[
                  [stripWest, tileSouth], [stripEast, tileSouth],
                  [stripEast, tileNorth], [stripWest, tileNorth],
                  [stripWest, tileSouth]
                ]]
              }
            });
          }
        }

        if (!hasEast) {
          for (let i = 0; i < numStrips; i++) {
            const opacity = (numStrips - i) / numStrips;
            const stripEast = tileEast - fadeWidth * i / numStrips;
            const stripWest = tileEast - fadeWidth * (i + 1) / numStrips;
            features.push({
              type: 'Feature',
              properties: { opacity },
              geometry: {
                type: 'Polygon',
                coordinates: [[
                  [stripWest, tileSouth], [stripEast, tileSouth],
                  [stripEast, tileNorth], [stripWest, tileNorth],
                  [stripWest, tileSouth]
                ]]
              }
            });
          }
        }

        if (!hasNorth) {
          for (let i = 0; i < numStrips; i++) {
            const opacity = (numStrips - i) / numStrips;
            const stripNorth = tileNorth - fadeWidth * i / numStrips;
            const stripSouth = tileNorth - fadeWidth * (i + 1) / numStrips;
            features.push({
              type: 'Feature',
              properties: { opacity },
              geometry: {
                type: 'Polygon',
                coordinates: [[
                  [tileWest, stripSouth], [tileEast, stripSouth],
                  [tileEast, stripNorth], [tileWest, stripNorth],
                  [tileWest, stripSouth]
                ]]
              }
            });
          }
        }

        if (!hasSouth) {
          for (let i = 0; i < numStrips; i++) {
            const opacity = (numStrips - i) / numStrips;
            const stripSouth = tileSouth + fadeWidth * i / numStrips;
            const stripNorth = tileSouth + fadeWidth * (i + 1) / numStrips;
            features.push({
              type: 'Feature',
              properties: { opacity },
              geometry: {
                type: 'Polygon',
                coordinates: [[
                  [tileWest, stripSouth], [tileEast, stripSouth],
                  [tileEast, stripNorth], [tileWest, stripNorth],
                  [tileWest, stripSouth]
                ]]
              }
            });
          }
        }
      });

      if (features.length > 0) {
        map.current.addSource('edge-fade-source', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          }
        });

        map.current.addLayer({
          id: 'edge-fade-layer',
          type: 'fill',
          source: 'edge-fade-source',
          paint: {
            'fill-color': bgColor,
            'fill-opacity': ['get', 'opacity']
          }
        });
      }
    }

    // Move symbol/label layers above custom tiles so place names remain visible
    if (hasCustomTiles) {
      const layers = map.current.getStyle().layers || [];
      layers.forEach(layer => {
        if (layer.type === 'symbol' && map.current.getLayer(layer.id)) {
          map.current.moveLayer(layer.id);
        }
      });
    }
  }, [config, getTileUrlTemplate]);

  // Initialize map when config loads
  useEffect(() => {
    if (!config || !mapContainer.current || map.current) return;

    const coordinates = config.route.coordinates;
    if (!coordinates?.length) return;

    // Calculate bounds
    const bounds = coordinates.reduce(
      (acc, coord) => ({
        minLng: Math.min(acc.minLng, coord[0]),
        maxLng: Math.max(acc.maxLng, coord[0]),
        minLat: Math.min(acc.minLat, coord[1]),
        maxLat: Math.max(acc.maxLat, coord[1])
      }),
      { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
    );

    // Get starting position and initial bearing for follow mode
    const startPosition = coordinates[0];
    const nextPoint = coordinates.length > 1 ? coordinates[1] : coordinates[0];
    const initialBearing = calculateBearing(startPosition[0], startPosition[1], nextPoint[0], nextPoint[1]);
    currentBearingRef.current = initialBearing;

    // Camera settings from config
    const cameraAltitude = config.display?.cameraAltitude || DEFAULT_CAMERA_ALTITUDE;
    const cameraDistance = config.display?.cameraDistance || DEFAULT_CAMERA_DISTANCE;

    // Calculate initial camera position (behind the start point)
    const behindBearing = (initialBearing + 180) % 360;
    const cameraPosition = offsetPosition(startPosition, behindBearing, cameraDistance);

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: TERRAIN_3D_STYLE,
      center: startPosition,
      zoom: config.display?.initialZoom || 13,
      pitch: 60,
      bearing: initialBearing,
      attributionControl: false,
      transformRequest: (url, resourceType) => {
        if (resourceType !== 'Tile') return undefined;
        if (tileLoadingModeRef.current !== 'prefetch') return undefined;
        const prefix = tileBasePathPrefixRef.current;
        if (!prefix || !url.startsWith(prefix)) return undefined;

        const match = url.match(/(\d+)_([\d-]+)_([\d-]+)\.png/i);
        if (!match) return undefined;
        const requestZ = Number(match[1]);
        const requestX = Number(match[2]);
        const requestY = Number(match[3]);
        const tileKey = `${requestZ}/${requestX}/${requestY}`;

        const tileKeySet = tileKeySetRef.current;
        if (!tilesIndexReadyRef.current) {
          const transparentUrl = getTransparentTileUrl();
          return transparentUrl ? { url: transparentUrl } : undefined;
        }

        const resolvedTileKey = resolveAvailableTileKey(requestZ, requestX, requestY);
        if (!resolvedTileKey || (tileKeySet && tileKeySet.size > 0 && !tileKeySet.has(resolvedTileKey))) {
          const transparentUrl = getTransparentTileUrl();
          return transparentUrl ? { url: transparentUrl } : undefined;
        }

        const cached = tileDataUrlRef.current.get(resolvedTileKey);
        if (cached) {
          return { url: cached };
        }

        if (resolvedTileKey !== tileKey && tileUrlTemplateRef.current) {
          const [rz, rx, ry] = resolvedTileKey.split('/').map(Number);
          let resolvedUrl = tileUrlTemplateRef.current
            .replace('{z}', rz)
            .replace('{x}', rx)
            .replace('{y}', ry);

          if (cacheBusterRef.current > 0) {
            resolvedUrl += `&_cb=${cacheBusterRef.current}`;
          }
          return { url: resolvedUrl };
        }

        return undefined;
      }
    });

    const handleMapError = (e) => {
      if (e?.error) {
        console.warn('[MAPBOX_ERROR]', e.error);
      }
    };

    map.current.on('load', async () => {
      // Add terrain as fallback if the style doesn't include it
      if (!map.current.getSource('mapbox-dem')) {
        map.current.addSource('mapbox-dem', {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14
        });
        map.current.setTerrain({ source: 'mapbox-dem', exaggeration: 2 });
      }

      // Calculate cumulative distances for scrubbing
      cumulativeDistancesRef.current = calculateCumulativeDistances(coordinates);

      // Add route to map
      addRouteToMap(coordinates, config?.route?.checkpoints || []);

      // Add custom tiles layer/source
      addProcessedTilesToMap();
      captureRouteStyleBaselines();

      // Disable user zoom controls (scroll, double-click, touch)
      map.current.scrollZoom.disable();
      map.current.doubleClickZoom.disable();
      map.current.touchZoomRotate.disable();

      // Auto-enable scrub mode so user can drag along route immediately.
      // The embedded viewer is a cinematic autoplay only: skip scrub, and keep
      // dragPan disabled so the frame never captures the parent page's scroll.
      map.current.dragPan.disable();
      if (!viewerOnly) {
        setIsScrubMode(true);
      }

      // Create puck marker at start position
      const puckEl = document.createElement('div');
      puckEl.className = 'gpx-puck';
      puckEl.innerHTML = '<div class="puck-inner"></div><div class="puck-trail"></div><div class="puck-tooltip">0 km</div>';
      puckMarkerRef.current = new mapboxgl.Marker(puckEl)
        .setLngLat(startPosition)
        .addTo(map.current);

      // Determine initial position: user's challenge progress or start of route
      let initialDistance = 0;
      if (config?.isChallenge && user?.uid && configId) {
        try {
          const participantRef = doc(db, 'gpxMapConfigs', configId, 'participants', user.uid);
          const participantSnap = await getDoc(participantRef);
          if (participantSnap.exists()) {
            initialDistance = participantSnap.data().currentDistanceMeters || 0;
          }
        } catch (err) {
          console.warn('[CHALLENGE] Could not fetch user progress for initial position:', err);
        }
      }

      if (initialDistance > 0) {
        // Move to user's current position on the route
        updatePositionAtDistance(initialDistance, false, { ignoreViewLimit: true });
      } else {
        // Set initial camera position using FreeCamera API for follow mode
        const camera = map.current.getFreeCameraOptions();
        camera.position = mapboxgl.MercatorCoordinate.fromLngLat(cameraPosition, cameraAltitude);
        camera.lookAtPoint(startPosition);
        map.current.setFreeCameraOptions(camera);
        applyRouteMapStyleAtDistance(0);
      }

      // Signal that map is ready for participant markers
      setMapReady(true);

      map.current.on('error', handleMapError);
    });

    return () => {
      checkpointMarkersRef.current.forEach((m) => m.remove());
      checkpointMarkersRef.current = [];
      activityMarkersRef.current.forEach((m) => m.remove());
      activityMarkersRef.current = [];
      openActivityPopupsRef.current.clear();
      currentActivityIdxRef.current = -1;
      if (map.current) {
        map.current.off('error', handleMapError);
        map.current.remove();
        map.current = null;
      }
    };
  }, [config, addProcessedTilesToMap, addRouteToMap, getTransparentTileUrl, captureRouteStyleBaselines, applyRouteMapStyleAtDistance, updatePositionAtDistance, resolveAvailableTileKey, viewerOnly]);

  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;
    applyRouteMapStyleAtDistance(currentDistanceRef.current || 0);
  }, [applyRouteMapStyleAtDistance, routeMapStyleChanges]);

  // Create activity markers and popups on the map (appears on LEFT of route)
  useEffect(() => {
    if (!mapReady || !map.current || !cumulativeDistancesRef.current || !config?.route?.coordinates) return;
    if (!activitySegments.length) return;

    const createMarkers = () => {
      const coordinates = config.route.coordinates;
      const mapboxStyle = 'samuelcollin3333/cmcyoppzj00ds01rf0gjpbt16';

      // Clean up old markers
      activityMarkersRef.current.forEach(m => m.remove());
      activityMarkersRef.current = [];
      openActivityPopupsRef.current.clear();

      activitySegments.forEach((seg) => {
        if (!map.current) return;

        // Position marker at midpoint of the activity segment on the route
        const position = getPositionAtDistance(
          coordinates, cumulativeDistancesRef.current, seg.midpoint
        );

        // Create invisible anchor element
        const el = document.createElement('div');
        el.className = 'activity-popup-anchor';

        // Build popup HTML
        const activity = seg.enrichedActivity;
        const distKm = activity
          ? (activity.distance / 1000).toFixed(1)
          : (seg.distanceMeters / 1000).toFixed(1);
        const duration = activity?.moving_time ? formatDuration(activity.moving_time) : '';
        const dateStr = seg.activityDate
          ? new Date(seg.activityDate.toDate ? seg.activityDate.toDate() : seg.activityDate).toLocaleDateString()
          : (activity?.start_date ? new Date(activity.start_date).toLocaleDateString() : '');

        // Activity name: use Strava name, fallback to date + distance
        const activityName = escapeHtml(
          activity?.name || (dateStr ? `${dateStr} \u00B7 ${distKm} km` : `${distKm} km`)
        );

        // Image priority: 1. Mythic Map, 2. Strava polyline, 3. Beige placeholder
        let imageHtml = '';
        const mythicUrl = activityGenerationMap[String(seg.activityId)]?.generatedImageUrl;
        if (mythicUrl) {
          imageHtml = `<div class="activity-popup-image"><img src="${escapeHtml(mythicUrl)}" alt="${activityName}"></div>`;
        } else if (activity?.map?.summary_polyline) {
          const encoded = encodeURIComponent(activity.map.summary_polyline);
          const pathStr = `path-5+FC5200-1(${encoded})`;
          const imgUrl = `https://api.mapbox.com/styles/v1/${mapboxStyle}/static/${pathStr}/auto/220x160@2x?padding=20&attribution=false&logo=false&access_token=${MAPBOX_ACCESS_TOKEN}`;
          imageHtml = `<div class="activity-popup-image"><img src="${escapeHtml(imgUrl)}" alt="${activityName}"></div>`;
        } else {
          imageHtml = `<div class="activity-popup-image activity-popup-image-empty"></div>`;
        }

        const popupHtml = `
          <div class="activity-popup">
            ${imageHtml}
            <div class="activity-popup-body">
              <div class="activity-popup-title">${activityName}</div>
              <div class="activity-popup-stats">${distKm} km${duration ? ' &middot; ' + duration : ''}</div>
              <div class="activity-popup-date">${escapeHtml(dateStr)}</div>
            </div>
          </div>
        `;

        // anchor: 'right' makes popup appear to the LEFT of the marker
        const popup = new mapboxgl.Popup({
          offset: 18,
          closeOnClick: false,
          closeButton: false,
          anchor: 'right'
        }).setHTML(popupHtml);

        const marker = new mapboxgl.Marker(el)
          .setLngLat(position)
          .setPopup(popup)
          .addTo(map.current);

        activityMarkersRef.current.push(marker);
      });
    };

    createMarkers();

    return () => {
      activityMarkersRef.current.forEach(m => m.remove());
      activityMarkersRef.current = [];
      openActivityPopupsRef.current.clear();
    };
  }, [mapReady, activitySegments, config, activityGenerationMap]);

  useEffect(() => {
    if (map.current && map.current.isStyleLoaded()) {
      if (tileLoadingMode === 'prefetch') {
        if (map.current.getLayer('custom-tiles-layer')) {
          map.current.removeLayer('custom-tiles-layer');
        }
        if (map.current.getSource('custom-tiles')) {
          map.current.removeSource('custom-tiles');
        }
      }
      addProcessedTilesToMap();
    }
  }, [tilesIndexVersion, tileLoadingMode, addProcessedTilesToMap]);

  // Update puck tooltip when distance changes
  useEffect(() => {
    if (puckMarkerRef.current) {
      const puckEl = puckMarkerRef.current.getElement();
      const tooltip = puckEl.querySelector('.puck-tooltip');
      if (tooltip) {
        const km = (currentDistance / 1000).toFixed(1);
        tooltip.textContent = `${km} km`;
      }
    }
  }, [currentDistance]);

  // Animation functions
  const startAnimation = useCallback(() => {
    if (!config?.route?.coordinates) return;
    flyoverRunIdRef.current += 1;
    if (flyoverAnimationRef.current) {
      cancelAnimationFrame(flyoverAnimationRef.current);
      flyoverAnimationRef.current = null;
    }

    // Calculate cumulative distances if not already done
    if (!cumulativeDistancesRef.current) {
      cumulativeDistancesRef.current = calculateCumulativeDistances(config.route.coordinates);
    }

    // Create puck marker if needed
    const coordinates = config.route.coordinates;
    const startPosition = currentDistance > 0 && cumulativeDistancesRef.current
      ? getPositionAtDistance(coordinates, cumulativeDistancesRef.current, currentDistance)
      : coordinates[0];

    if (!puckMarkerRef.current && map.current) {
      const puckEl = document.createElement('div');
      puckEl.className = 'gpx-puck';
      puckEl.innerHTML = '<div class="puck-inner"></div><div class="puck-trail"></div>';
      puckMarkerRef.current = new mapboxgl.Marker(puckEl)
        .setLngLat(startPosition)
        .addTo(map.current);
    }

    // Re-enable map drag (was disabled in scrub mode)
    if (map.current) {
      map.current.dragPan.enable();
    }

    posthog?.capture('challenge_flyover_started', { page: 'challenge_detail', challenge_id: configId, start_distance_meters: currentDistance });
    setIsAnimating(true);
    setIsPaused(false);
    setIsScrubMode(false);
  }, [config, currentDistance, posthog, configId]);

  // Embedded viewer: auto-play a smooth flyover from the route start to the
  // rider's projected distance. Uses the same self-contained rAF + eased
  // updatePositionAtDistance loop as the activity replay (handleActivityFlyover)
  // so the camera glides without the per-frame React effect churn of the legacy
  // animate loop (which re-seeds position from state and jitters/reverses).
  // Fires exactly once via hasAutoStartedRef.
  useEffect(() => {
    if (!viewerOnly || !autoplay || hasAutoStartedRef.current) return;
    if (!mapReady || !map.current || !config?.route?.coordinates) return;
    if (!cumulativeDistancesRef.current || checkpointDistances.length === 0) return;
    hasAutoStartedRef.current = true;

    // The embed lazy-mounts into a frame that may have sized after Mapbox
    // initialized; sync the canvas to the container before the flyover.
    map.current.resize();

    const cumulative = cumulativeDistancesRef.current;
    const totalDistance = cumulative[cumulative.length - 1] || 0;
    const end = Math.max(0, Math.min(Number(projectedDistanceMeters) || 0, totalDistance));

    updatePositionAtDistance(0, false);
    if (end <= 1) return undefined;

    const runId = flyoverRunIdRef.current + 1;
    flyoverRunIdRef.current = runId;
    // Mirror the activity-replay pacing (handleActivityFlyover) so the embed
    // feels identical: ~0.65 ms/m, clamped to a watchable 5-26 s.
    const durationMs = Math.max(5000, Math.min(26000, end * 0.65));
    const startTime = performance.now();

    const step = (timestamp) => {
      if (flyoverRunIdRef.current !== runId || !map.current) return;
      const t = Math.max(0, Math.min(1, (timestamp - startTime) / durationMs));
      const eased = t < 0.5 ? 2 * t * t : 1 - (Math.pow(-2 * t + 2, 2) / 2);
      updatePositionAtDistance(end * eased);
      if (t < 1) {
        flyoverAnimationRef.current = requestAnimationFrame(step);
      } else {
        flyoverAnimationRef.current = null;
        updatePositionAtDistance(end, false);
      }
    };
    flyoverAnimationRef.current = requestAnimationFrame(step);

    return () => {
      if (flyoverAnimationRef.current) {
        cancelAnimationFrame(flyoverAnimationRef.current);
        flyoverAnimationRef.current = null;
      }
    };
  }, [viewerOnly, autoplay, mapReady, config, checkpointDistances.length, projectedDistanceMeters, updatePositionAtDistance]);

  const pauseAnimation = useCallback(() => {
    flyoverRunIdRef.current += 1;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (flyoverAnimationRef.current) {
      cancelAnimationFrame(flyoverAnimationRef.current);
      flyoverAnimationRef.current = null;
    }
    posthog?.capture('challenge_flyover_paused', { page: 'challenge_detail', challenge_id: configId, paused_at_distance_meters: currentDistance });
    setIsPaused(true);
    setIsAnimating(false);
    setIsScrubMode(true);

    // Ensure cumulative distances are calculated for scrubbing
    if (!cumulativeDistancesRef.current && config?.route?.coordinates) {
      cumulativeDistancesRef.current = calculateCumulativeDistances(config.route.coordinates);
    }

    // Create puck marker if needed (for scrubbing before animation starts)
    if (!puckMarkerRef.current && config?.route?.coordinates && map.current) {
      const startPosition = currentDistance > 0 && cumulativeDistancesRef.current
        ? getPositionAtDistance(config.route.coordinates, cumulativeDistancesRef.current, currentDistance)
        : config.route.coordinates[0];

      const puckEl = document.createElement('div');
      puckEl.className = 'gpx-puck';
      puckEl.innerHTML = '<div class="puck-inner"></div><div class="puck-trail"></div>';
      puckMarkerRef.current = new mapboxgl.Marker(puckEl)
        .setLngLat(startPosition)
        .addTo(map.current);
    }

    // Disable map's default drag behavior so we can use it for scrubbing
    if (map.current) {
      map.current.dragPan.disable();
    }
  }, [config, currentDistance, posthog, configId]);

  const stopAnimation = useCallback(() => {
    flyoverRunIdRef.current += 1;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (flyoverAnimationRef.current) {
      cancelAnimationFrame(flyoverAnimationRef.current);
      flyoverAnimationRef.current = null;
    }

    // Reset state
    setIsAnimating(false);
    setIsPaused(false);
    setAnimationProgress(0);
    currentDistanceRef.current = 0;
    setCurrentDistance(0);
    setIsScrubMode(false);
    updateCheckpointPopups(0);
    updateActivityPopups(0);
    currentActivityIdxRef.current = -1;

    // Clear bearing history for fresh start
    bearingHistoryRef.current = [];
    currentBearingRef.current = 0;

    // Re-enable map drag
    if (map.current) {
      map.current.dragPan.enable();
    }

    // Move puck back to start
    if (puckMarkerRef.current && config?.route?.coordinates) {
      puckMarkerRef.current.setLngLat(config.route.coordinates[0]);
    }

    // Zoom back out to show the visible portion of the route
    if (map.current && config?.route?.coordinates) {
      const coordinates = config.route.coordinates;
      const bounds = new mapboxgl.LngLatBounds();
      const maxView = maxViewableDistanceRef.current;
      const cumDist = cumulativeDistancesRef.current;

      if (config?.isChallenge && maxView > 0 && maxView < Infinity && cumDist) {
        for (let i = 0; i < coordinates.length; i++) {
          bounds.extend(coordinates[i]);
          if (cumDist[i] >= maxView) break;
        }
      } else {
        coordinates.forEach(coord => bounds.extend(coord));
      }
      map.current.fitBounds(bounds, {
        padding: 50,
        duration: 1000,
        bearing: 0,
        pitch: 0
      });
    }
  }, [config]);

  // Scrub/drag handlers for manual route navigation
  const handleScrubStart = useCallback((clientY) => {
    if (!isScrubMode || !cumulativeDistancesRef.current) return;

    isDraggingRef.current = true;
    dragStartYRef.current = clientY;
    dragStartDistanceRef.current = currentDistanceRef.current; // Use ref to avoid stale closure
  }, [isScrubMode]);

  const handleScrubMove = useCallback((clientY) => {
    if (!isDraggingRef.current || !cumulativeDistancesRef.current) return;

    // Calculate vertical movement (positive = dragging down = move forward on route)
    // This makes it feel like you're "pulling" the map under the puck
    const deltaY = clientY - dragStartYRef.current;

    // Convert pixels to meters along route
    const deltaDistance = deltaY * DRAG_METERS_PER_PIXEL;
    const newDistance = dragStartDistanceRef.current + deltaDistance;

    updatePositionAtDistance(newDistance);
  }, [updatePositionAtDistance]);

  const handleScrubEnd = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  // Mouse event handlers for scrubbing
  const handleMouseDown = useCallback((e) => {
    if (!isScrubMode) return;
    e.preventDefault();
    handleScrubStart(e.clientY);
  }, [isScrubMode, handleScrubStart]);

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    handleScrubMove(e.clientY);
  }, [handleScrubMove]);

  const handleMouseUp = useCallback(() => {
    handleScrubEnd();
  }, [handleScrubEnd]);

  // Touch event handlers for scrubbing
  const handleTouchStart = useCallback((e) => {
    if (!isScrubMode) return;
    const touch = e.touches[0];
    handleScrubStart(touch.clientY);
  }, [isScrubMode, handleScrubStart]);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    handleScrubMove(touch.clientY);
  }, [handleScrubMove]);

  const handleTouchEnd = useCallback(() => {
    handleScrubEnd();
  }, [handleScrubEnd]);

  // Attach global mouse/touch event listeners for scrub mode
  useEffect(() => {
    if (!isScrubMode) return;

    const handleGlobalMouseMove = (e) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();
    const handleGlobalTouchMove = (e) => {
      if (isDraggingRef.current) {
        e.preventDefault();
        const touch = e.touches[0];
        handleScrubMove(touch.clientY);
      }
    };
    const handleGlobalTouchEnd = () => handleTouchEnd();

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    window.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isScrubMode, handleMouseMove, handleMouseUp, handleScrubMove, handleTouchEnd]);

  // Animation loop with camera smoothing
  useEffect(() => {
    if (!isAnimating || isPaused || !config?.route?.coordinates || !map.current) return;

    const coordinates = config.route.coordinates;
    const speed = config.display?.animationSpeedKmh || 100;
    const metersPerSecond = (speed * 1000) / 3600;

    // Ensure cumulative distances are calculated
    if (!cumulativeDistancesRef.current) {
      cumulativeDistancesRef.current = calculateCumulativeDistances(coordinates);
    }

    const totalDistance = cumulativeDistancesRef.current[cumulativeDistancesRef.current.length - 1];
    const maxView = maxViewableDistanceRef.current;
    const animationEnd = (config?.isChallenge && maxView > 0 && maxView < Infinity) ? Math.min(totalDistance, maxView) : totalDistance;
    let currentDist = currentDistance;
    let lastTime = performance.now();

    const animate = (time) => {
      if (!map.current) return;

      const deltaTime = (time - lastTime) / 1000;
      lastTime = time;

      currentDist += metersPerSecond * deltaTime;

      if (currentDist >= animationEnd) {
        // Animation complete (clamped to fog boundary)
        currentDist = animationEnd;
        currentDistanceRef.current = currentDist;
        setCurrentDistance(currentDist);
        setAnimationProgress(currentDist / totalDistance);

        const finalPos = getPositionAtDistance(coordinates, cumulativeDistancesRef.current, animationEnd);
        if (puckMarkerRef.current) {
          puckMarkerRef.current.setLngLat(finalPos);
        }

        updateCheckpointPopups(currentDist);
        updateActivityPopups(currentDist);
        updateRouteGradient(currentDist);
        setIsAnimating(false);
        setIsPaused(false);
        animationRef.current = null;
        return;
      }

      // Get position at current distance
      const position = getPositionAtDistance(
        coordinates,
        cumulativeDistancesRef.current,
        currentDist
      );

      // Calculate target bearing and apply smoothing
      const targetBearing = getBearingAtDistance(
        coordinates,
        cumulativeDistancesRef.current,
        currentDist
      );

      // Apply bearing smoothing for smooth camera rotation
      // Smoothing factor decreases (more smoothing) as speed increases
      const smoothingFactor = Math.max(0.01, 0.08 * (100 / Math.max(100, speed)));

      let prevBearing = currentBearingRef.current;
      let delta = targetBearing - prevBearing;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      const newBearing = prevBearing + delta * smoothingFactor;
      const normalizedBearing = ((newBearing % 360) + 360) % 360;

      // Add to history buffer for additional smoothing
      bearingHistoryRef.current.push(normalizedBearing);
      if (bearingHistoryRef.current.length > BEARING_HISTORY_SIZE) {
        bearingHistoryRef.current.shift();
      }

      // Calculate weighted average of history
      let weightedSum = 0;
      let weightTotal = 0;
      const history = bearingHistoryRef.current;
      for (let i = 0; i < history.length; i++) {
        const weight = i + 1;
        let histBearing = history[i];
        if (history.length > 1) {
          const refBearing = history[history.length - 1];
          let diff = histBearing - refBearing;
          if (diff > 180) histBearing -= 360;
          if (diff < -180) histBearing += 360;
        }
        weightedSum += histBearing * weight;
        weightTotal += weight;
      }
      const bearing = ((weightedSum / weightTotal % 360) + 360) % 360;
      currentBearingRef.current = bearing;

      // Update puck marker
      if (puckMarkerRef.current) {
        puckMarkerRef.current.setLngLat(position);
      }

      // Use FreeCamera API to position camera behind the puck
      const behindBearing = (bearing + 180) % 360;
      const cameraPosition = offsetPosition(position, behindBearing, cameraDistanceRef.current);

      const camera = map.current.getFreeCameraOptions();
      camera.position = mapboxgl.MercatorCoordinate.fromLngLat(cameraPosition, cameraAltitudeRef.current);
      camera.lookAtPoint(position);
      map.current.setFreeCameraOptions(camera);

      currentDistanceRef.current = currentDist;
      setCurrentDistance(currentDist);
      setAnimationProgress(currentDist / totalDistance);
      updateCheckpointPopups(currentDist);
      updateActivityPopups(currentDist);
      updateRouteGradient(currentDist);

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating, isPaused, config, currentDistance]);

  if (isLoading) {
    return (
      <div className="gpx-map-page">
        <div className="gpx-map-loading">
          <div className="loading-spinner" />
          <p>Loading map...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gpx-map-page">
        <div className="gpx-map-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="back-button">
            <IconArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gpx-map-page" style={themeCssVars}>
      {!viewerOnly && (
      <div className="gpx-map-stats">
        <h1 className="gpx-page-title">{(challengeCatalogEntry?.title || config?.name) ? `${challengeCatalogEntry?.title || config.name} Challenge` : 'Mythic Challenge'}</h1>
        <span className="gpx-page-title-divider" />
        {isAdmin && (
          <button
            className="stats-advanced-toggle"
            onClick={() => setShowStatsBar(prev => !prev)}
          >
            <span className="settings-icon">⚙</span> Settings
          </button>
        )}
        {showStatsBar && (
          <>
            <span>{config.route.stats.distance} km</span>
            <span>{config.route.stats.elevationGain}m gain</span>
            <button
              className="tile-loading-toggle"
              onClick={() => setTileLoadingMode((prev) => (prev === 'dynamic' ? 'prefetch' : 'dynamic'))}
              title={tileLoadingMode === 'dynamic'
                ? 'Switch to preloading tiles in the background'
                : 'Switch to Mapbox dynamic tile loading'}
            >
              {tileLoadingMode === 'dynamic' ? 'Tiles: Dynamic' : 'Tiles: Preload'}
            </button>
            {tileLoadingMode === 'prefetch' && tileProgress.total > 0 && (
              <div className="tile-progress-container">
                <div className="tile-progress-bar">
                  <div
                    className="tile-progress-fill"
                    style={{ width: `${(tileProgress.loaded / tileProgress.total) * 100}%` }}
                  />
                </div>
                <span className="tile-progress-text">
                  {tileProgress.loaded}/{tileProgress.total}
                </span>
              </div>
            )}
            {tileLoadingMode === 'prefetch' && (
              <button
                className="clear-cache-button"
                onClick={clearTileCache}
                title="Clear cached tiles and re-download"
              >
                Clear Cache
              </button>
            )}
            {Array.isArray(config?.route?.checkpoints) && config.route.checkpoints.length > 0 && (
              <button
                className="checkpoints-button"
                onClick={() => setShowCheckpoints(true)}
                title="View checkpoints"
              >
                {config.route.checkpoints.length} checkpoints
              </button>
            )}
          </>
        )}
      </div>
      )}

      <div className="gpx-map-wrapper">
        <div
          ref={mapContainer}
          className={`gpx-map-container ${isScrubMode ? 'scrub-mode' : ''}`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
        {!viewerOnly && config?.route?.coordinates && config.route.coordinates.length > 1 && (
          <RouteOverviewMinimap
            coordinates={config.route.coordinates}
            currentPosition={
              cumulativeDistancesRef.current
                ? getPositionAtDistance(
                    config.route.coordinates,
                    cumulativeDistancesRef.current,
                    currentDistance
                  )
                : null
            }
            theme={challengeTheme}
            switchCheckpointIndex={(() => {
              if (switchCheckpointIndex == null || !Array.isArray(config.route.checkpoints)) return null;
              const cp = config.route.checkpoints[switchCheckpointIndex];
              if (!cp || typeof cp.lon !== 'number' || typeof cp.lat !== 'number') return null;
              // Find nearest coordinate index to the switch checkpoint
              let bestIdx = 0;
              let bestSq = Infinity;
              const coords = config.route.coordinates;
              for (let i = 0; i < coords.length; i++) {
                const dx = coords[i][0] - cp.lon;
                const dy = coords[i][1] - cp.lat;
                const sq = dx * dx + dy * dy;
                if (sq < bestSq) { bestSq = sq; bestIdx = i; }
              }
              return bestIdx;
            })()}
            routeTitle={challengeCatalogEntry?.title || config?.name || 'Mythic Challenge'}
            routeDistanceKm={Math.round(
              config?.route?.stats?.distance ||
              (getChallengeTotalDistanceMeters(config?.route) / 1000) ||
              0
            )}
            startLabel={config?.route?.checkpoints?.[0]?.name || 'Start'}
            endLabel={finalCheckpointName}
          />
        )}
        {!viewerOnly && config?.route?.coordinates && config.route.coordinates.length > 1 && (
          <MobileRouteGlobeButton
            coordinates={config.route.coordinates}
            theme={challengeTheme}
            switchCheckpointIndex={(() => {
              if (switchCheckpointIndex == null || !Array.isArray(config.route.checkpoints)) return null;
              const cp = config.route.checkpoints[switchCheckpointIndex];
              if (!cp || typeof cp.lon !== 'number' || typeof cp.lat !== 'number') return null;
              let bestIdx = 0;
              let bestSq = Infinity;
              const coords = config.route.coordinates;
              for (let i = 0; i < coords.length; i++) {
                const dx = coords[i][0] - cp.lon;
                const dy = coords[i][1] - cp.lat;
                const sq = dx * dx + dy * dy;
                if (sq < bestSq) { bestSq = sq; bestIdx = i; }
              }
              return bestIdx;
            })()}
            routeTitle={challengeCatalogEntry?.title || config?.name || 'Mythic Challenge'}
            routeDistanceKm={Math.round(
              config?.route?.stats?.distance ||
              (getChallengeTotalDistanceMeters(config?.route) / 1000) ||
              0
            )}
            startLabel={config?.route?.checkpoints?.[0]?.name || 'Start'}
            endLabel={finalCheckpointName}
          />
        )}
      </div>

      {/* Challenge join banner */}
      {!viewerOnly && config?.isChallenge && !hasJoined && (
        activeChallengeInfo ? (
          <div className="join-challenge-banner join-challenge-banner-locked">
            <span>
              <IconLock size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Finish {activeChallengeInfo.title} before starting another journey.
            </span>
            <button
              onClick={() => navigate(`/challenge/${activeChallengeInfo.challengeId}`)}
            >
              Go to {activeChallengeInfo.title}
              <IconChevronRight size={14} style={{ marginLeft: 4 }} />
            </button>
          </div>
        ) : (
          <div className="join-challenge-banner">
            <span>Join this challenge to track your progress!</span>
            <button onClick={handleJoinClick}>
              Join Challenge
            </button>
          </div>
        )
      )}

      {/* Completed banner — surfaces the Restart action */}
      {!viewerOnly && config?.isChallenge && hasJoined && isCompleted && (
        <div className="join-challenge-banner join-challenge-banner-completed">
          <span>
            <IconTrophy size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            You completed this journey. Restart for a fresh run any time.
          </span>
          <button
            onClick={requestRestartChallenge}
            disabled={isRestarting}
          >
            Restart Journey
          </button>
        </div>
      )}

      {/* Challenge leaderboard */}
      {!viewerOnly && config?.isChallenge && (
        <div className="challenge-leaderboard">
          <div
            className="leaderboard-track"
            onClick={(e) => {
              // Click anywhere on the track to navigate to that position on the route
              const track = e.currentTarget;
              const rect = track.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              const progress = Math.max(0, Math.min(1, clickX / rect.width));
              const totalDistance = cumulativeDistancesRef.current?.[cumulativeDistancesRef.current.length - 1];
              if (totalDistance) {
                updatePositionAtDistance(progress * totalDistance, false);
              }
            }}
          >
            {elevationPathD && (
              <svg className="leaderboard-elevation" viewBox="0 0 1000 100" preserveAspectRatio="none">
                <path d={elevationPathD} />
              </svg>
            )}
            <div className="leaderboard-line" />
            {/* Transparent puck showing current scrub/camera position */}
            {(() => {
              const totalDistance = totalRouteDistanceM || 1;
              const puckProgress = Math.min((currentDistance || 0) / totalDistance, 1);
              return (
                <div
                  className="leaderboard-puck"
                  style={{ left: `${puckProgress * 100}%` }}
                  title={`Current view: ${(currentDistance / 1000).toFixed(1)} km`}
                />
              );
            })()}
            {finishersData.nonFinishers.map((participant, index) => {
              const totalDistance = totalRouteDistanceM || 1;
              const progress = (participant.currentDistanceMeters || 0) / totalDistance;
              return (
                <div
                  key={participant.oddc}
                  className={`leaderboard-avatar ${participant.oddc === user?.uid ? 'is-current-user' : ''}`}
                  style={{ left: `${Math.min(progress * 100, 100)}%` }}
                  title={`${participant.displayName}: ${((participant.currentDistanceMeters || 0) / 1000).toFixed(1)} km`}
                  onClick={(e) => {
                    // Click on avatar to centre the map on that participant's position
                    e.stopPropagation();
                    const participantDistance = participant.currentDistanceMeters || 0;
                    updatePositionAtDistance(participantDistance, false);
                  }}
                >
                  {participant.photoURL ? (
                    <img src={participant.photoURL} alt={participant.displayName} />
                  ) : (
                    <span>{(participant.displayName || '?')[0]}</span>
                  )}
                  {index === 0 && participant.currentDistanceMeters > 0 && <div className="leader-badge">1st</div>}
                </div>
              );
            })}
            {finishersData.count > 0 && (
              <div
                className="leaderboard-finishers-indicator"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFinishersModal(true);
                }}
                title={`${finishersData.count} finisher${finishersData.count !== 1 ? 's' : ''}`}
              >
                <IconTrophy size={14} />
                <span>{finishersData.count}</span>
              </div>
            )}
          </div>
          <div className="leaderboard-labels">
            <span>Start</span>
            <span>{config.route?.stats?.distance || '?'} km</span>
          </div>
        </div>
      )}

      {/* Challenge Stats */}
      {!viewerOnly && config?.isChallenge && challengeStats && (
        <div className="challenge-stats-section">
          <div className="challenge-stats-grid">
            <div className="challenge-stat-card">
              <IconUsers size={22} className="challenge-stat-icon" />
              <span className="challenge-stat-value">{challengeStats.athletes}</span>
              <span className="challenge-stat-label">Athletes</span>
            </div>
            <div className="challenge-stat-card">
              <IconRuler2 size={22} className="challenge-stat-icon" />
              <span className="challenge-stat-value">{(challengeStats.distance / 1000).toFixed(1)} km</span>
              <span className="challenge-stat-label">Total Distance</span>
            </div>
            <div className="challenge-stat-card">
              <IconClock size={22} className="challenge-stat-icon" />
              <span className="challenge-stat-value">{formatDuration(challengeStats.movingTime)}</span>
              <span className="challenge-stat-label">Total Time</span>
            </div>
            <div className="challenge-stat-card">
              <IconFlag size={22} className="challenge-stat-icon" />
              <span className="challenge-stat-value">{challengeStats.checkpointsVisited}</span>
              <span className="challenge-stat-label">Checkpoints Visited</span>
            </div>
            <div className="challenge-stat-card">
              <IconMountain size={22} className="challenge-stat-icon" />
              <span className="challenge-stat-value">{Math.round(challengeStats.elevationGain).toLocaleString()} m</span>
              <span className="challenge-stat-label">Elevation Gain</span>
            </div>
            <div className="challenge-stat-card">
              <IconActivity size={22} className="challenge-stat-icon" />
              <span className="challenge-stat-value">{challengeStats.activities}</span>
              <span className="challenge-stat-label">Activities</span>
            </div>
          </div>
        </div>
      )}

      {/* Travelog Timeline */}
      {!viewerOnly && config?.isChallenge && Array.isArray(config?.route?.checkpoints) && config.route.checkpoints.length > 0 && (
        <div className="travelog-timeline" ref={journeySectionRef}>
          <div className="travelog-header">
            {travelogOwner.photoURL && (
              <img src={travelogOwner.photoURL} alt="" className="travelog-profile-pic" referrerPolicy="no-referrer" />
            )}
            <h2 className="travelog-title">
              {travelogOwner.name
                ? `${makePossessive(travelogOwner.name)} ${challengeShortName} Travelog`
                : `Your ${challengeShortName} Travelog`}
            </h2>
            {isCompleted && (
              <button className="travelog-print-btn" onClick={handlePrintCertificate} title="Save as image" disabled={isExporting}>
                <IconDownload size={16} />
                <span>{isExporting ? 'Saving...' : 'Save your travelog'}</span>
              </button>
            )}
            {hasJoined && user?.uid === participantData?.userId && (
              <button className="travelog-manage-btn" onClick={() => setShowManageModal(true)} title="Manage activities">
                <IconSettings size={16} />
              </button>
            )}
          </div>
          <div className="travelog-print-logo">
            <img src={JapaneseLogo} alt="Mythic Maps" />
            <span>Mythic Maps</span>
          </div>

          {/* User stats */}
          {hasJoined && userStats && (
            <div className="travelog-user-stats">
              {isCompleted && (
                <div className="travelog-completed-wrap" ref={completedBannerRef}>
                  <div className="travelog-completed-banner">
                    {getFinisherMedalUrl(challengeCatalogEntry)
                      ? (
                        <button
                          type="button"
                          className="completed-medal-btn"
                          onClick={() => setShowMedalModal(true)}
                          aria-label={`View ${challengeShortName} finisher medal`}
                        >
                          <img src={getFinisherMedalUrl(challengeCatalogEntry)} alt="" className="completed-medal" crossOrigin="anonymous" />
                        </button>
                      )
                      : <IconTrophy size={24} />}
                    <div>
                      <div className="completed-title">Challenge Complete!</div>
                      <div className="completed-subtitle">You conquered the {challengeShortName}</div>
                      <div className="completed-reward">
                        +{diamondStats.earned} <IconDiamond size={14} /> earned
                      </div>
                    </div>
                  </div>
                  {showConfetti && (
                    <div className="confetti-container" aria-hidden="true">
                      {Array.from({ length: 40 }, (_, i) => {
                        const palette = [
                          challengeTheme.primary,
                          challengeTheme.primaryDark,
                          challengeTheme.secondary,
                          challengeTheme.tint,
                          '#d4c4b0',
                          '#f5e6e6',
                        ];
                        return (
                          <div key={i} className="confetti-piece" style={{
                            '--x': `${10 + Math.random() * 80}%`,
                            '--delay': `${Math.random() * 0.4}s`,
                            '--color': palette[i % palette.length],
                          }} />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="challenge-stats-grid">
                <div className="challenge-stat-card">
                  <IconCalendar size={22} className="challenge-stat-icon" />
                  <span className="challenge-stat-value">{userStats.startedStr}</span>
                  <span className="challenge-stat-label">Started</span>
                </div>
                {isCompleted && (
                  <div className="challenge-stat-card">
                    <IconCalendar size={22} className="challenge-stat-icon" />
                    <span className="challenge-stat-value">{userStats.finishedStr}</span>
                    <span className="challenge-stat-label">Finished</span>
                  </div>
                )}
                {!isCompleted && (
                  <div className="challenge-stat-card">
                    <IconRoute size={22} className="challenge-stat-icon" />
                    <span className="challenge-stat-value">{userStats.remainingKm} km</span>
                    <span className="challenge-stat-label">Remaining</span>
                  </div>
                )}
                <div className="challenge-stat-card">
                  <IconClock size={22} className="challenge-stat-icon" />
                  <span className="challenge-stat-value">{userStats.daysPassed}</span>
                  <span className="challenge-stat-label">Days</span>
                </div>
                <div className="challenge-stat-card">
                  <IconActivity size={22} className="challenge-stat-icon" />
                  <span className="challenge-stat-value">{userStats.activityCount}</span>
                  <span className="challenge-stat-label">Activities</span>
                </div>
                <div className="challenge-stat-card">
                  <IconFlag size={22} className="challenge-stat-icon" />
                  <span className="challenge-stat-value">{userStats.checkpointCount}</span>
                  <span className="challenge-stat-label">Checkpoints</span>
                </div>
                <div className="challenge-stat-card">
                  <IconMountain size={22} className="challenge-stat-icon" />
                  <span className="challenge-stat-value">{userStats.elevationGain.toLocaleString()} m</span>
                  <span className="challenge-stat-label">Elevation</span>
                </div>
                <div className="challenge-stat-card">
                  <IconDiamond size={22} className="challenge-stat-icon" />
                  <span className="challenge-stat-value">{userStats.diamondsEarned}</span>
                  <span className="challenge-stat-label">Diamonds</span>
                </div>
              </div>
            </div>
          )}

          {/* Masonry timeline grid */}
          <div className="travelog-masonry" style={{ '--cols': snakeColCount }}>
            {/* Start bookend — connects to first cell via left-side bend */}
            <div className="tm-bookend tm-bookend-start tm-bookend-left">
              <div className="travelog-bookend-marker">
                <span>Start</span>
                <span className="travelog-bookend-sub">0 km</span>
              </div>
            </div>

            {/* Snake rows */}
            {snakeRows.map((row, rIdx) => {
              const maxDistInRow = Math.max(...row.events.map(e => e.distance));
              const actStartEvent = row.events.find(e => e.isActStart);

              return (
                <React.Fragment key={`row-${rIdx}`}>
                  {/* Act divider — with vertical line pass-through on the bend side */}
                  {actStartEvent && (() => {
                    const prevRow = rIdx > 0 ? snakeRows[rIdx - 1] : null;
                    // Line side: follows the bend between previous row and this row
                    // Row 0 with act divider: line comes from Start bookend on the left
                    const dividerSide = prevRow
                      ? (prevRow.direction === 'ltr' ? 'right' : 'left')
                      : 'left';
                    const prevMaxDist = prevRow
                      ? Math.max(...prevRow.events.map(e => e.distance))
                      : 0;
                    const dividerLineActive = rIdx === 0 || userProgress >= prevMaxDist;
                    return (
                      <div className={`tm-act-divider tm-act-divider-${dividerSide} ${dividerLineActive ? 'tm-bend-active' : 'tm-bend-inactive'}`}>
                        <div className="travelog-act-banner">
                          {actStartEvent.act.name && (
                            <span className="travelog-act-name">{actStartEvent.act.name}</span>
                          )}
                          <span className="travelog-act-style">{actStartEvent.act.style}</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* RTL incomplete last row: empty cells go BEFORE events (leftmost columns) */}
                  {rIdx === snakeRows.length - 1 && row.direction === 'rtl' && row.events.length < snakeColCount && Array.from(
                    { length: snakeColCount - row.events.length },
                    (_, i) => {
                      const emptyCount = snakeColCount - row.events.length;
                      const isFirstEmpty = i === 0;
                      const prevRow = rIdx > 0 ? snakeRows[rIdx - 1] : null;
                      const prevMaxDist = prevRow ? Math.max(...prevRow.events.map(e => e.distance)) : 0;
                      const lineActive = userProgress >= prevMaxDist;
                      const lineClass = lineActive ? 'tm-line-active' : 'tm-line-inactive';
                      return (
                        <div
                          key={`empty-${rIdx}-${i}`}
                          className={[
                            'tm-cell tm-empty-line',
                            isFirstEmpty && 'tm-bookend-conn-end tm-bookend-conn-left',
                            isFirstEmpty && isCompleted && 'tm-bookend-conn-active',
                            isFirstEmpty && (lineActive ? 'tm-bend-active' : 'tm-bend-inactive'),
                          ].filter(Boolean).join(' ')}
                        >
                          <div className="tm-dot-row">
                            <div className={`tm-dot-line ${lineClass}`} />
                          </div>
                        </div>
                      );
                    }
                  )}

                  {/* Event cells */}
                  {row.events.map((event, cellIdx) => {
                    const isFirst = cellIdx === 0;
                    const isLast = cellIdx === row.events.length - 1;
                    const isActive = userProgress >= event.distance;
                    const lineClass = isActive ? 'tm-line-active' : 'tm-line-inactive';
                    const dotClass = `tm-dot ${isActive ? 'tm-dot-active' : 'tm-dot-inactive'} ${event.type === 'progress' ? 'tm-dot-current' : ''}`;
                    const eventKey = event.type === 'checkpoint' ? `cp-${event.globalIdx}` : event.type === 'activity' ? `act-${event.activityId}` : 'progress';

                    // Determine if this cell is at a U-bend point
                    const isLastRow = rIdx === snakeRows.length - 1;
                    const isFirstRow = rIdx === 0;

                    // bend-end: the cell where the horizontal line ends and turns down
                    const isBendEnd = !isLastRow && (
                      (row.direction === 'ltr' && isLast) ||
                      (row.direction === 'rtl' && isFirst)
                    );
                    // bend-start: the cell where the line turns up from the previous row
                    const isBendStart = !isFirstRow && (
                      (row.direction === 'ltr' && isFirst) ||
                      (row.direction === 'rtl' && isLast)
                    );

                    // Bookend connections: first cell of first row, last cell of last row
                    // Start bookend always connects from the left (row 0 = LTR, starts at left)
                    const isBookendStart = isFirstRow && isFirst;
                    // End bookend on event cells: only when the last row is FULL.
                    // For incomplete rows, empty cells handle the connection to the grid edge.
                    const isLastRowFull = row.events.length === snakeColCount;
                    const isBookendEnd = isLastRow && isLastRowFull && (
                      (row.direction === 'ltr' && isLast) ||
                      (row.direction === 'rtl' && isFirst)
                    );
                    const bookendEndSide = isBookendEnd
                      ? (row.direction === 'ltr' ? 'right' : 'left')
                      : null;

                    // Which side of the grid does the bend run in?
                    const bendSide = isBendEnd
                      ? (row.direction === 'ltr' ? 'right' : 'left')
                      : isBendStart
                        ? (row.direction === 'ltr' ? 'left' : 'right')
                        : null;

                    // Is the bend connector line active (gold) or inactive (gray)?
                    const prevRow = rIdx > 0 ? snakeRows[rIdx - 1] : null;
                    const prevMaxDist = prevRow ? Math.max(...prevRow.events.map(e => e.distance)) : 0;
                    const bendLineActive = isBendEnd
                      ? userProgress >= maxDistInRow
                      : isBendStart
                        ? userProgress >= prevMaxDist
                        : false;

                    // On bend/bookend cells, DON'T hide the edge line — it extends into the margin.
                    // Must check ALL roles: e.g. bend-start on the right needs the right line,
                    // bend-end on the left needs the left line, etc.
                    // For incomplete last rows, edge lines must stay visible so the line
                    // flows into the empty cells that carry it to the grid edge.
                    // LTR: last event's RIGHT line → empty cells carry rightward
                    // RTL: first event's LEFT line → empty cells (rendered before) carry leftward
                    const incompleteLastLTR = isLastRow && !isLastRowFull && row.direction === 'ltr' && isLast;
                    const incompleteLastRTL = isLastRow && !isLastRowFull && row.direction === 'rtl' && isFirst;
                    const needsLeftLine = (isBendStart && bendSide === 'left') || (isBendEnd && bendSide === 'left') || isBookendStart || (isBookendEnd && bookendEndSide === 'left') || incompleteLastRTL;
                    const needsRightLine = (isBendStart && bendSide === 'right') || (isBendEnd && bendSide === 'right') || (isBookendEnd && bookendEndSide === 'right') || incompleteLastLTR;
                    const hideLeft = isFirst && !needsLeftLine;
                    const hideRight = isLast && !needsRightLine;

                    return (
                      <div
                        key={eventKey}
                        className={[
                          'tm-cell',
                          event.type === 'progress' && 'tm-cell-progress',
                          isBendEnd && `tm-bend-end tm-bend-${bendSide}`,
                          isBendStart && `tm-bend-start tm-bend-${bendSide}`,
                          (isBendEnd || isBendStart) && (bendLineActive ? 'tm-bend-active' : 'tm-bend-inactive'),
                          isBookendStart && 'tm-bookend-conn-start',
                          isBookendEnd && `tm-bookend-conn-end tm-bookend-conn-${bookendEndSide}`,
                          isBookendEnd && isCompleted && 'tm-bookend-conn-active',
                        ].filter(Boolean).join(' ')}
                      >
                        {/* Dot row above card */}
                        <div className="tm-dot-row">
                          <div className={`tm-dot-line ${hideLeft ? 'tm-hidden' : ''} ${lineClass}`} />
                          <div className={dotClass} />
                          <div className={`tm-dot-line ${hideRight ? 'tm-hidden' : ''} ${lineClass}`} />
                        </div>

                        {/* Card */}
                        {event.type === 'checkpoint' && (
                          <TimelineCheckpointCard event={event} onSelect={(cp) => {
                            posthog?.capture('challenge_checkpoint_viewed', {
                              page: 'challenge_detail',
                              challenge_id: configId,
                              checkpoint_index: cp?.globalIdx,
                              checkpoint_name: cp?.name || `Checkpoint ${(cp?.globalIdx || 0) + 1}`,
                            });
                            setSelectedCheckpoint(cp);
                          }} />
                        )}
                        {event.type === 'activity' && (
                          <TimelineActivityCard
                            event={event}
                            onCreateMap={handleCreateMapFromJourney}
                            onOpenGeneratedMap={handleOpenGeneratedMap}
                            onFlyover={handleActivityFlyover}
                          />
                        )}
                        {event.type === 'progress' && (
                          <div className="tm-progress-card">
                            <div className="tl-story-text">
                              You are here &mdash; <strong>{(userProgress / 1000).toFixed(1)} km</strong>
                            </div>
                          </div>
                        )}

                        {/* Story text (not for progress — already shown in card above) */}
                        {event.type !== 'progress' && (
                          <div className="tm-story">
                            <TimelineStoryBlock event={event} onCreateMap={handleCreateMapFromJourney} userProgress={userProgress} />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Pad empty cells for incomplete rows (skip RTL last row — handled above) */}
                  {row.events.length < snakeColCount && !(rIdx === snakeRows.length - 1 && row.direction === 'rtl') && Array.from(
                    { length: snakeColCount - row.events.length },
                    (_, i) => {
                      const emptyCount = snakeColCount - row.events.length;
                      const isLastEmpty = i === emptyCount - 1;
                      const isLastRowHere = rIdx === snakeRows.length - 1;
                      // LTR last row: empty cells carry the horizontal line rightward to the grid edge
                      if (isLastRowHere && row.direction === 'ltr') {
                        const maxDist = Math.max(...row.events.map(e => e.distance));
                        const lineActive = userProgress >= maxDist;
                        const lineClass = lineActive ? 'tm-line-active' : 'tm-line-inactive';
                        return (
                          <div
                            key={`empty-${rIdx}-${i}`}
                            className={[
                              'tm-cell tm-empty-line',
                              isLastEmpty && 'tm-bookend-conn-end tm-bookend-conn-right',
                              isLastEmpty && isCompleted && 'tm-bookend-conn-active',
                              isLastEmpty && (lineActive ? 'tm-bend-active' : 'tm-bend-inactive'),
                            ].filter(Boolean).join(' ')}
                          >
                            <div className="tm-dot-row">
                              <div className={`tm-dot-line ${lineClass}`} />
                            </div>
                          </div>
                        );
                      }
                      return <div key={`empty-${rIdx}-${i}`} className="tm-cell tm-empty" />;
                    }
                  )}
                </React.Fragment>
              );
            })}

            {/* End bookend — always uses direction-based side (incomplete rows still span full grid) */}
            {(() => {
              const lastRow = snakeRows[snakeRows.length - 1];
              const lastRowDir = lastRow ? lastRow.direction : 'ltr';
              const endSide = lastRowDir === 'ltr' ? 'right' : 'left';
              return (
                <div className={`tm-bookend tm-bookend-end tm-bookend-${endSide} ${isCompleted ? 'tm-bookend-active' : ''}`} ref={isCompleted ? kyotoBookendRef : undefined}>
                  <div className="travelog-bookend-marker travelog-bookend-end" style={{ position: 'relative' }}>
                    <span>{finalCheckpointName}</span>
                    <span className="travelog-bookend-sub">{config.route?.stats?.distance || '?'} km</span>
                    {showKyotoConfetti && (
                      <div className="confetti-container" aria-hidden="true">
                        {Array.from({ length: 40 }, (_, i) => {
                          const palette = [
                            challengeTheme.primary,
                            challengeTheme.primaryDark,
                            challengeTheme.secondary,
                            challengeTheme.tint,
                            '#d4c4b0',
                            '#f5e6e6',
                          ];
                          return (
                            <div key={i} className="confetti-piece" style={{
                              '--x': `${10 + Math.random() * 80}%`,
                              '--delay': `${Math.random() * 0.4}s`,
                              '--color': palette[i % palette.length],
                            }} />
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Empty state when user joined but no activities */}
          {hasJoined && !participantData?.activities?.length && (
            <div className="travelog-empty">
              Complete activities on Strava to fill your travelog!
            </div>
          )}
        </div>
      )}

      {/* Checkpoints modal */}
      {showCheckpoints && Array.isArray(config?.route?.checkpoints) && (
        <div className="join-modal-overlay" onClick={() => setShowCheckpoints(false)}>
          <div className="join-modal checkpoints-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCheckpoints(false)}>
              <IconX size={20} />
            </button>
            <h3>Checkpoints</h3>
            <p>Tap a marker on the map to see details.</p>
            <div className="checkpoint-modal-list">
              {config.route.checkpoints.map((cp, i) => (
                <div key={`${cp.lon},${cp.lat},${cp.index ?? i}`} className="checkpoint-modal-item">
                  <div className="checkpoint-modal-title">
                    {i + 1}. {cp.name || `Checkpoint ${i + 1}`}
                  </div>
                  {(cp.type || cp.sym) && (
                    <div className="checkpoint-modal-meta">
                      {[cp.type, cp.sym].filter(Boolean).join(' • ')}
                    </div>
                  )}
                  {cp.imageUrl && (
                    <img
                      className="checkpoint-modal-image"
                      src={cp.imageUrl}
                      alt={cp.name || `Checkpoint ${i + 1}`}
                    />
                  )}
                  {cp.desc && <div className="checkpoint-modal-desc">{cp.desc}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Finisher medal lightbox */}
      {showMedalModal && (
        <FinisherMedalModal
          medalUrl={getFinisherMedalUrl(challengeCatalogEntry)}
          title={challengeCatalogEntry?.title || config?.name}
          slug={challengeCatalogEntry?.slug}
          accent={challengeTheme?.primary}
          onClose={() => setShowMedalModal(false)}
        />
      )}

      {/* Checkpoint detail modal */}
      {selectedCheckpoint && (
        <div className="join-modal-overlay" onClick={() => setSelectedCheckpoint(null)}>
          <div className="checkpoint-detail-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedCheckpoint(null)}>
              <IconX size={20} />
            </button>
            {selectedCheckpoint.imageUrl && (
              <img
                className="checkpoint-detail-image"
                src={selectedCheckpoint.imageUrl}
                alt={selectedCheckpoint.name || `Checkpoint ${selectedCheckpoint.globalIdx + 1}`}
              />
            )}
            <div className="checkpoint-detail-content">
              <div className="checkpoint-detail-label">Checkpoint {selectedCheckpoint.globalIdx + 1}</div>
              <h3 className="checkpoint-detail-name">
                {selectedCheckpoint.name || `Checkpoint ${selectedCheckpoint.globalIdx + 1}`}
              </h3>
              <div className="checkpoint-detail-distance">
                {(selectedCheckpoint.cpDistance / 1000).toFixed(1)} km along the route
              </div>
              {selectedCheckpoint.desc && (
                <div className="checkpoint-detail-story">{selectedCheckpoint.desc}</div>
              )}
              <div className="checkpoint-share-section">
                {selectedCheckpoint.imageUrl && (
                  <button className="checkpoint-share-download" onClick={handleCheckpointDownload} disabled={isDownloadingCheckpoint}>
                    <IconDownload size={16} /> {isDownloadingCheckpoint ? 'Downloading...' : 'Download Image'}
                  </button>
                )}
                <div className="checkpoint-share-divider"><span>Share on social media</span></div>
                <div className="checkpoint-share-grid">
                  <button className="checkpoint-share-option" onClick={() => handleCheckpointShare('twitter')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                    <span>Twitter</span>
                  </button>
                  <button className="checkpoint-share-option" onClick={() => handleCheckpointShare('facebook')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                    <span>Facebook</span>
                  </button>
                  <button className="checkpoint-share-option" onClick={() => handleCheckpointShare('instagram')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /></svg>
                    <span>Instagram</span>
                  </button>
                  <button className="checkpoint-share-option" onClick={() => handleCheckpointShare('tiktok')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" /></svg>
                    <span>TikTok</span>
                  </button>
                  <button className="checkpoint-share-option" onClick={() => handleCheckpointShare('reddit')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" /></svg>
                    <span>Reddit</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Activities Modal */}
      {showManageModal && (
        <div className="join-modal-overlay" onClick={() => setShowManageModal(false)}>
          <div className="join-modal manage-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowManageModal(false)}>
              <IconX size={20} />
            </button>
            <h3>Manage Activities</h3>
            {participantData?.activities?.length > 0 ? (
              <div className="manage-activity-list">
                {[...participantData.activities]
                  .sort((a, b) => {
                    const dateA = a.activityDate?.toDate ? a.activityDate.toDate().getTime() : (a.activityDate?.seconds ? a.activityDate.seconds * 1000 : 0);
                    const dateB = b.activityDate?.toDate ? b.activityDate.toDate().getTime() : (b.activityDate?.seconds ? b.activityDate.seconds * 1000 : 0);
                    return dateB - dateA;
                  })
                  .map((activity) => {
                    const enriched = journeyActivities.find(ja => String(ja.id) === String(activity.activityId));
                    const name = enriched?.name || 'Activity';
                    const distKm = ((activity.distanceMeters || 0) / 1000).toFixed(1);
                    const date = activity.activityDate?.toDate
                      ? activity.activityDate.toDate().toLocaleDateString()
                      : activity.activityDate?.seconds
                        ? new Date(activity.activityDate.seconds * 1000).toLocaleDateString()
                        : '';
                    const isDeleting = deletingActivityId === String(activity.activityId);
                    return (
                      <div key={activity.activityId} className="manage-activity-row">
                        <div className="manage-activity-info">
                          <span className="manage-activity-name">{name}</span>
                          <span className="manage-activity-meta">{distKm} km{date ? ` · ${date}` : ''}</span>
                        </div>
                        <button
                          className="manage-delete-btn"
                          onClick={() => handleRemoveActivity(String(activity.activityId))}
                          disabled={!!deletingActivityId}
                          title="Remove activity"
                        >
                          {isDeleting ? <IconLoader2 size={16} className="manage-spinner" /> : <IconTrash size={16} />}
                        </button>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="manage-empty">No activities imported yet.</p>
            )}
          </div>
        </div>
      )}

      {/* Finishers Modal */}
      {showFinishersModal && finishersData.count > 0 && (
        <div className="join-modal-overlay" onClick={() => setShowFinishersModal(false)}>
          <div className="join-modal finishers-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowFinishersModal(false)}>
              <IconX size={20} />
            </button>
            <h3><IconTrophy size={20} /> Finishers</h3>
            <p className="finishers-subtitle">{finishersData.count} athlete{finishersData.count !== 1 ? 's' : ''} completed this challenge</p>

            {finishersData.userRank && (
              <div className="finishers-user-rank">
                You finished <strong>#{finishersData.userRank}</strong> of {finishersData.count}
              </div>
            )}

            <div className="finishers-section">
              <h4>First to Finish</h4>
              <div className="finishers-list">
                {finishersData.firstFive.map((f, i) => (
                  <div key={f.oddc} className={`finisher-row ${f.oddc === user?.uid ? 'is-current-user' : ''}`}>
                    <span className="finisher-rank">#{i + 1}</span>
                    <div className="finisher-avatar">
                      {f.photoURL ? (
                        <img src={f.photoURL} alt={f.displayName} />
                      ) : (
                        <span>{(f.displayName || '?')[0]}</span>
                      )}
                    </div>
                    <div className="finisher-info">
                      <span className="finisher-name">{f.displayName || 'Unknown'}</span>
                      <span className="finisher-date">
                        {f.finishDate
                          ? f.finishDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {finishersData.count > 5 && (
              <div className="finishers-section">
                <h4>Most Recent</h4>
                <div className="finishers-list">
                  {finishersData.lastFive.map((f, i) => (
                    <div key={f.oddc} className={`finisher-row ${f.oddc === user?.uid ? 'is-current-user' : ''}`}>
                      <span className="finisher-rank">#{finishersData.count - i}</span>
                      <div className="finisher-avatar">
                        {f.photoURL ? (
                          <img src={f.photoURL} alt={f.displayName} />
                        ) : (
                          <span>{(f.displayName || '?')[0]}</span>
                        )}
                      </div>
                      <div className="finisher-info">
                        <span className="finisher-name">{f.displayName || 'Unknown'}</span>
                        <span className="finisher-date">
                          {f.finishDate
                            ? f.finishDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auto-append challenge progress popup */}
      {showAutoAppendPopup && (() => {
        const activities = participantData?.activities || [];
        const lastActivity = activities[activities.length - 1];
        const distanceKm = lastActivity ? (lastActivity.distanceMeters / 1000).toFixed(1) : '0.0';
        const totalRouteM = totalRouteDistanceM;
        const currentProgress = participantData?.currentDistanceMeters || 0;
        const remainingKm = Math.max(0, (totalRouteM - currentProgress) / 1000).toFixed(0);
        const endCheckpoint = config?.route?.checkpoints?.find(cp => cp.isEnd);
        const destination = endCheckpoint?.name || 'the finish';
        const challengeDisplayName = challengeCatalogEntry?.title || config?.name || configId;
        const challengeLink = `https://mythicmaps.ai/challenge/${configId}`;
        const previewMessage = `Today I moved ${distanceKm} km towards ${destination}. I have ${remainingKm}km left on ${challengeDisplayName} challenge. Follow my progress or try to beat me at ${challengeLink}`;

        return (
          <AutoAppendChallengePopup
            user={user}
            configId={configId}
            challengeName={challengeDisplayName}
            destination={destination}
            previewMessage={previewMessage}
            alreadyEnabled={autoAppendAlreadyEnabled}
            onClose={() => { posthog?.capture('challenge_auto_append_popup_dismissed', { page: 'challenge_detail', challenge_id: configId }); setShowAutoAppendPopup(false); }}
            onEnabled={() => { posthog?.capture('challenge_auto_append_enabled', { page: 'challenge_detail', challenge_id: configId }); setShowAutoAppendPopup(false); }}
          />
        );
      })()}

      {/* Multi-step onboarding modal */}
      {onboardingStep > 0 && (
        <div className="join-modal-overlay" onClick={() => setOnboardingStep(0)}>
          <div className="join-modal onboarding-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setOnboardingStep(0)}>
              <IconX size={20} />
            </button>

            {/* Step indicator dots */}
            <div className="onboarding-steps">
              {[1, 2, 3].map(step => (
                <div
                  key={step}
                  className={`onboarding-dot${onboardingStep >= step ? ' active' : ''}${onboardingStep === step ? ' current' : ''}`}
                />
              ))}
            </div>

            {/* Step 1: Connect Strava */}
            {onboardingStep === 1 && (
              <div className="onboarding-step-content">
                <h3>Join Challenge</h3>
                <p>Connect with Strava to join this challenge and track your progress!</p>
                <button className="strava-connect-button" onClick={handleStravaConnect}>
                  <img src={stravaConnectButton} alt="Connect with Strava" />
                </button>
              </div>
            )}

            {/* Step 2: Profile Review */}
            {onboardingStep === 2 && (
              <div className="onboarding-step-content">
                <h3>Your Challenge Profile</h3>
                <p>Your name and photo will be visible to other participants on the leaderboard.</p>

                <div className="onboarding-profile-preview">
                  <div className="onboarding-avatar-wrapper">
                    <img
                      src={profilePhotoPreview || '/default-avatar.png'}
                      alt="Profile"
                      className="onboarding-avatar-img"
                    />
                    <label className="onboarding-avatar-upload">
                      <IconCamera size={14} />
                      <input type="file" accept="image/*" onChange={handlePhotoSelect} hidden />
                    </label>
                  </div>
                  <input
                    className="onboarding-name-input"
                    value={profileName}
                    onChange={e => setProfileName(e.target.value)}
                    placeholder="Your display name"
                  />
                </div>

                {joinError && <p className="join-error">{joinError}</p>}

                <button
                  className="join-submit-button"
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                >
                  {isSavingProfile ? 'Saving...' : 'Join Challenge'}
                </button>
              </div>
            )}

            {/* Step 3: Import Activity */}
            {onboardingStep === 3 && (
              <div className="onboarding-step-content">
                <h3>Get a Head Start!</h3>
                <p>Would you like to import your latest Strava activity to start your journey toward {finalCheckpointName}?</p>

                {loadingLatestActivity ? (
                  <div className="onboarding-loading">
                    <div className="onboarding-spinner" />
                    <span>Fetching your latest activity...</span>
                  </div>
                ) : latestActivity ? (
                  <div className="onboarding-activity-card">
                    <div className="onboarding-activity-icon">
                      <IconRun size={24} />
                    </div>
                    <div className="onboarding-activity-info">
                      <div className="onboarding-activity-name">{latestActivity.name}</div>
                      <div className="onboarding-activity-stats">
                        {(latestActivity.distance / 1000).toFixed(1)} km
                        {latestActivity.moving_time && (
                          <> &middot; {formatDuration(latestActivity.moving_time)}</>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="onboarding-no-activity">No recent activities found on Strava.</p>
                )}

                {latestActivity && (
                  <button
                    className="join-submit-button"
                    onClick={handleImportActivity}
                    disabled={importingActivity}
                  >
                    {importingActivity
                      ? 'Importing...'
                      : `Import & Move ${(latestActivity.distance / 1000).toFixed(1)} km`
                    }
                  </button>
                )}
                <button className="onboarding-skip-btn" onClick={handleSkipImport}>
                  Start from my next activity <IconChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Restart confirmation modal */}
      {showRestartModal && (
        <div className="restart-modal-overlay" onClick={closeRestartModal}>
          <div className="restart-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Restart {challengeCatalogEntry?.title || config?.name || 'this challenge'}?</h2>
            <p className="restart-modal-body">
              Your completion trophy stays in your collection. Progress resets to zero kilometers and you can run the route again.
            </p>
            {restartError && (
              <div className="restart-modal-error">
                <span>{restartError}</span>
              </div>
            )}
            <div className="restart-modal-actions">
              <button
                type="button"
                className="restart-modal-cancel"
                onClick={closeRestartModal}
                disabled={isRestarting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="restart-modal-confirm"
                onClick={confirmRestartChallenge}
                disabled={isRestarting}
              >
                {isRestarting ? 'Restarting...' : 'Restart journey'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GpxMapPage;
